// View schemas: the shapes screens render. One request per screen. The
// simulator and the future API both produce these; the client parses them.
// See docs/design/contracts/API.md.

import { MatchupCue } from './matchup-cue';
import { z } from 'zod';
import { DraftMode, DraftType, WaiverSettings } from '../rules/schemas';
import { pageOf } from './envelope';
import { StatsFreshness } from './clock';
import { UserPrefs } from './prefs';
import { AchievementShows, AchievementTone, FoundAchievement } from './achievements';

import { TeamBadge } from '../teams/badges';
import { TeamBanner } from '../teams/banners';
import { Face } from '../teams/faces';
import { LeagueHat, WeeklyHatSchema } from '../teams/hats';
import { SkillBandId } from './bots';
import { EmailInvite } from './email-invites';
const Iso = z.string();
const Hex = z.string().regex(/^#[0-9a-f]{6}$/i);

// ── Shared refs ───────────────────────────────────────────────────────────

export const PeriodRef = z.object({
  index: z.number().int().min(1),
  label: z.string(),
  start: Iso,
  end: Iso,
  isCurrent: z.boolean(),
  kind: z.enum(['regular', 'playoff', 'consolation', 'off']),
});
export type PeriodRef = z.infer<typeof PeriodRef>;

export const TeamRef = z.object({
  id: z.string(),
  name: z.string(),
  abbreviation: z.string(),
  ownerName: z.string(),
  colors: z.object({ primary: Hex, secondary: Hex }),
  /**
   * The team's badge, drawn on its colors in place of its initials: an icon
   * or an emoji from the site's sets (`@fsp/domain` `BADGE_ICONS`,
   * `BADGE_EMOJI`), a bot's face on a seat a bot holds (B-464), a face its
   * manager made (`Face`, B-488), or null for the initials (DIRECTION U25).
   * Added 2026-10-18; faces 2026-10-20 and 2026-10-24.
   */
  badge: TeamBadge.default(null),
  /**
   * The picture behind the team where a screen is about it (My Team's header,
   * each side of Game Day): a banner from the site's catalog
   * (`TEAM_BANNERS`), `seasons` for one that follows the calendar, or null
   * for none (PLAN-2026-10-24-team-banners). Which scene shows on a day is
   * `bannerSceneOn`, which also gives a seat a bot holds the bot's own when
   * this is null. Added 2026-10-24.
   */
  banner: TeamBanner.nullable().default(null),
  record: z.object({ wins: z.number(), losses: z.number(), ties: z.number() }),
  isMine: z.boolean(),
  /**
   * Championships in this league's history, for the crown next to the name.
   *
   * NULL MEANS NOT COMPUTED, and it is not the same as zero. The league store
   * has no history engine yet and answered `0` — a claim, in a field a screen
   * renders as "no titles yet", about a league that may have five (audit 3.7).
   * A server that has not counted says so; a client renders nothing for null
   * and a crown for a number.
   */
  titles: z.number().int().nullable().default(null),
});
export type TeamRef = z.infer<typeof TeamRef>;

export const LeagueRef = z.object({
  id: z.string(),
  name: z.string(),
  sport: z.string(),
  season: z.number().int(),
  status: z.enum(['setup', 'drafting', 'in-season', 'playoffs', 'complete', 'archived']),
  teamCount: z.number().int(),
  myTeamId: z.string().nullable(),
  isCommissioner: z.boolean(),
  commissionerName: z.string(),
  /**
   * Nobody holds `commissioner` in this league.
   *
   * On the REF rather than only on the setup view, because the hub is where
   * somebody finds out: a card that says "Nobody runs it" is a sentence about
   * a sentinel, and a card that names a commissioner who deleted their account
   * is worse. Derived from the membership rows in both implementations, never
   * stored.
   */
  needsCommissioner: z.boolean(),
  /**
   * When the draft is, while it is still to come or under way: in setup, and
   * in drafting until the draft completes. Null once it has run — a drafted
   * league waits in `drafting` until its season opens, and a date on it then
   * is a draft that already happened (the review of round 2, NUMBERS-7).
   */
  draftAt: Iso.nullable(),
  /**
   * How the league plays a week (`formatRefOf`, PLAN-2026-10-16-league-formats):
   * the format's id; whether a head-to-head week adds a game against the league
   * median; whether each regular-season week is two games, against two
   * opponents (`twoOpponents`, Q6, added 2026-11-01); whether best ball sets
   * the lineup; and an elimination league's knockouts a week, null in any
   * other. On the ref so that every view says which screen to draw without a
   * client reading the rules. Added 2026-10-16.
   */
  format: z
    .object({
      id: z.string(),
      medianGame: z.boolean(),
      twoOpponents: z.boolean().default(false),
      bestBall: z.boolean(),
      knockoutsPerPeriod: z.number().int().nullable(),
    })
    .default({
      id: 'h2h-points',
      medianGame: false,
      twoOpponents: false,
      bestBall: false,
      knockoutsPerPeriod: null,
    }),
  /**
   * The league's season before this one and after it (PLAN-2026-10-27-the-
   * next-season §2.1): each season is a league of its own, chained to the
   * last. The last is the league this season was started from, null in a
   * league's first; the next is the season started from this one, null until
   * a commissioner starts it. Each is null too for a reader who is not in
   * that season, who could not open it. Added 2026-10-27.
   */
  lastSeasonId: z.string().nullable().default(null),
  nextSeasonId: z.string().nullable().default(null),
  /**
   * The helper tools in force in this league (`effectiveTools`, RULES_SCHEMA
   * §15c): the trade read-out and the lineup total are off while projections
   * are. What a switch turns off is withheld from every read, so a client
   * reads this to say why a figure is not there and leaves out its column,
   * rather than drawing a dash. Every helper on by default. Added 2026-10-29.
   */
  tools: z
    .object({
      projections: z.boolean(),
      suggestions: z.boolean(),
      tradeAnalysis: z.boolean(),
      sandbox: z.boolean(),
    })
    .default({ projections: true, suggestions: true, tradeAnalysis: true, sandbox: true }),
  /**
   * The weekly hat (PLAN-2026-10-30-the-weekly-hat): the latest week's, worn
   * by `wearers` until the next week's is decided, picked by anybody running
   * one of `pickers` (a reader may when `myTeamId` is one of them); null while
   * the league has none or turned it off. Added 2026-10-30.
   */
  hat: LeagueHat.nullable().default(null),
});
export type LeagueRef = z.infer<typeof LeagueRef>;

export const InjuryView = z.object({
  status: z.string(),
  name: z.string(),
  severity: z.enum(['none', 'warn', 'likely-out', 'out']),
  note: z.string().nullable(),
  /**
   * When the injury report this designation is from was first read as it
   * stands, which a client writes beside a designation it spells out (B-52):
   * the source carries no filing date, and it is read once a day. Null for a
   * designation his club's roster gives (injured reserve, a suspension),
   * which no report files. Every read the server answers carries it; absent
   * from the practice league's simulator, whose players are on no report, and
   * from a draft room prepared before it. Added 2026-11-01.
   */
  readAt: Iso.nullable().optional(),
});

export const PlayerSummary = z.object({
  id: z.string(),
  name: z.string(),
  shortName: z.string(),
  positions: z.array(z.string()),
  proTeam: z.string().nullable(),
  /**
   * His place on his club's depth chart: the chart's column, in its own word,
   * and his rank there, 1 the first string (ICD-P01's `depth`). Null on no
   * current chart; absent where a read does not carry it.
   */
  depth: z
    .object({ position: z.string(), rank: z.number().int().min(1) })
    .nullable()
    .optional(),
  injury: InjuryView.nullable(),
  onBye: z.boolean(),
});
export type PlayerSummary = z.infer<typeof PlayerSummary>;

/** The player's pro game in this period, from their fantasy team's point of view. */
export const GameStatus = z.object({
  state: z.enum(['pre', 'live', 'final', 'bye', 'none']),
  /** 'Sun 1:00' | 'Q3 8:12' | 'Final' | 'Bye' */
  label: z.string(),
  opponent: z.string().nullable(),
  home: z.boolean(),
  startAt: Iso.nullable(),
  /** 'DET 21–GB 17' when live or final. */
  score: z.string().nullable(),
});
export type GameStatus = z.infer<typeof GameStatus>;

/**
 * What a projection is made of, for the "why" popover (DETAIL_LEVELS.md rung 3).
 *
 * Facts only. This is the offer behind letting a manager decide how much they
 * trust a prediction (docs/decisions/2026-09-12-tools-not-instructions.md): it
 * says where the number came from so they can discount it. It never says what
 * to do about it, and it never carries another player's name.
 */
export const ProjectionBasis = z.object({
  /**
   * Points per game so far this season. Null when there are no games to
   * average (`periods` is 0): an average of nothing is not zero, and a 0.0
   * beside a projection of 14 reads as a contradiction. Nullable since
   * 2026-09-25 (round 2 finish, B-165).
   */
  seasonAvg: z.number().nullable(),
  /** How many games that average rests on. Zero before his first, which the popover says out loud. */
  periods: z.number().int(),
  /** The pro opponent this period, as the row shows it: 'at MIN', 'vs GB'. */
  opponent: z.string().nullable(),
  /**
   * What moved the number, in words: a stated zero's reason ('On bye', 'Out',
   * 'Not on the active roster', 'His team has already played') or the matchup
   * when it moved the line ('Matchup +4%'). Null when nothing did. Facts only.
   */
  adjustment: z.string().nullable(),
  /**
   * Why there is NO projection, when `projection` is null because none was
   * made: 'Not projected — no recent NFL games'. Null whenever there is a number,
   * and whenever nobody has said why there is not. Not a zero: a zero is a
   * number, and says he will not score. Added 2026-09-25 (N3).
   */
  withheld: z.string().nullable(),
});
export type ProjectionBasis = z.infer<typeof ProjectionBasis>;

/**
 * One stat's part in a player's points (`scorePlayer`'s breakdown, folded by
 * stat): what a points number is made of, for the "why" popover on a score and
 * the game log's breakdown. Facts only, in the league's own scoring.
 */
export const ScoreItem = z.object({
  stat: z.string(),
  /** The stat's name, and "bonus" for a bonus: "Passing Yards", "Passing Yards bonus". */
  label: z.string(),
  value: z.number(),
  points: z.number(),
});
export type ScoreItem = z.infer<typeof ScoreItem>;

// ── Lineup ────────────────────────────────────────────────────────────────

export const LineupRowView = z.object({
  slot: z.string(),
  slotIndex: z.number().int(),
  slotKind: z.enum(['starter', 'bench', 'reserve', 'taxi']),
  player: PlayerSummary.nullable(),
  game: GameStatus.nullable(),
  /**
   * How his opponent has done against his position this season, in this
   * league's scoring (PLAN-2026-10-03-research-tools.md §4). Null for an
   * empty row, a bye, or an opponent yet to play. Added 2026-10-03.
   */
  matchup: MatchupCue.nullable().default(null),
  /** The stored projection in this league's scoring. The same rule as `MatchupRowView.projection`. */
  projection: z.number().nullable(),
  points: z.number().nullable(),
  locked: z.boolean(),
  lockAt: Iso.nullable(),
  /** Slots this player may occupy; empty for an empty row. */
  eligibleSlots: z.array(z.string()),
  health: z.enum(['ok', 'warn', 'out', 'bye', 'empty', 'illegal']),
  statLine: z.string().nullable(),
  /**
   * How `points` was made, stat by stat, in this league's scoring: each
   * scoring stat's value and what it earned, a bonus as its own item, items
   * worth nothing left out; the items are exact, and a league that rounds its
   * scores rounds only `points`. Null whenever `points` is, and when no line
   * is behind it (a 0 for a game he had no line in). Added 2026-10-11, so a
   * score can say where it came from (DETAIL_LEVELS rung 3).
   */
  breakdown: z.array(ScoreItem).nullable().default(null),
  /** Where the projection came from. Null on an empty row. */
  basis: ProjectionBasis.nullable(),
  /**
   * His points a game this season, the lineup table's Avg: a fact, served
   * whether or not the league keeps projections, where the basis it was read
   * from is withheld with them (RULES_SCHEMA §15c). Added 2026-10-29.
   */
  seasonAvg: z.number().nullable().default(null),
  /**
   * Whether his injury status lets him go on this league's IR (D9): false on
   * an empty row, and in a league with no IR.
   */
  reserveEligible: z.boolean().default(false),
  /**
   * Whether he may go on this league's taxi squad now
   * (PLAN-2026-10-27-the-next-season §2.7): within its seasons in the pro
   * league, and before the squad closes, at the first kickoff of the week the
   * league's rules name (`roster.taxi.deadlinePeriod`). False on an empty row
   * and in a league with no taxi squad. Added 2026-10-28.
   */
  taxiEligible: z.boolean().default(false),
  /**
   * In best ball (scenario Q2, B-363), the starting slot this player's points
   * count in, by the points so far (`bestBallCounting`): the rows stay where
   * the lineup stores them, and this says who counts. Null for a player who
   * does not count, before the week has points, and in any other league.
   * Added 2026-10-16.
   */
  countsAs: z.string().nullable().default(null),
});
export type LineupRowView = z.infer<typeof LineupRowView>;

export const LineupProblem = z.object({
  code: z.enum([
    'empty-slot',
    'starter-out',
    'starter-bye',
    'starter-doubtful',
    'reserve-illegal',
    'roster-illegal',
  ]),
  message: z.string(),
  rowSlot: z.string().nullable(),
  rowSlotIndex: z.number().int().nullable(),
  /**
   * Whether this slot can be filled from the bench, and how many players could
   * fill it. A count, deliberately, and never a name
   * (docs/decisions/2026-09-12-tools-not-instructions.md): flagging a slot that
   * cannot score is the site's job, picking who plays it is the manager's.
   */
  swap: z
    .object({
      /** Bench players eligible for this slot who can still score. At least one. */
      candidates: z.number().int().positive(),
    })
    .nullable(),
  /**
   * The one move that clears it, when there is one (D9, LINEUP.md "IR, and a
   * roster that breaks its rules"): `to-reserve`, the Out starter in
   * `rowSlot` onto an open IR spot; `off-reserve`, the player on IR in
   * `rowSlot` into an open bench spot, or else an open starting slot he can
   * play; `drop`, a player dropped to make room. A capability, never a name:
   * which player goes is the manager's.
   */
  fix: z.enum(['to-reserve', 'off-reserve', 'drop']).nullable().default(null),
});

export type LineupProblem = z.infer<typeof LineupProblem>;

export const LineupHealth = z.object({
  /** `locked` once every starter that locks has: nothing on this lineup can change. */
  status: z.enum(['ok', 'warn', 'error', 'locked']),
  headline: z.string(),
  /** Only slots that can still change: a locked starter's bye or Out is not raised (B-221). */
  problems: z.array(LineupProblem),
  /**
   * When the next starter still to lock locks, or null when none is left.
   * Under the per-player rule each starter locks at his own kickoff, so this
   * moves through a Sunday (B-221).
   */
  locksAt: Iso.nullable(),
});
export type LineupHealth = z.infer<typeof LineupHealth>;

export const LineupView = z.object({
  leagueId: z.string(),
  teamId: z.string(),
  /**
   * Whose lineup this is. Any member may read any team's (ICD-S01 §4), and
   * `team.isMine` is how a client knows whether this one is theirs to change —
   * without it, a team page had to compare ids fetched from somewhere else.
   */
  team: TeamRef,
  period: PeriodRef,
  starters: z.array(LineupRowView),
  bench: z.array(LineupRowView),
  reserve: z.array(LineupRowView),
  /**
   * The league's IR rule in the rules' own words, for the IR band: "1 IR spot
   * for players listed as out, injured reserve or suspended. …". Null in a
   * league with no IR.
   */
  reserveRule: z.string().nullable().default(null),
  /**
   * The league's taxi squad in a sentence, for the taxi band: "3 Taxi spots
   * for players with 2 or fewer seasons in the league, filled until the
   * season's first kickoff. …". Null in a league with none. Added 2026-10-28.
   */
  taxiRule: z.string().nullable().default(null),
  health: LineupHealth,
  /**
   * The starters' total by the one rule every read uses (`sideTotal`, B-166),
   * the matchup side's `projected`: points so far plus what is still to come.
   * A starter whose game is over, or who has none this period, counts his
   * points (0 when he has none) whatever his projection; an empty starting slot
   * counts 0; bench and reserve rows are not in it. Null while a starter still
   * to play has no projection — the sum of a list with a hole in it is not a
   * number — and for a lineup with nobody in it.
   */
  projectedTotal: z.number().nullable(),
  /**
   * The matchup's own score for this side: E3's scoring call over the
   * starters' lines, and for a final week the lines it was settled from (B-168).
   * Null until a starter has a line.
   */
  liveTotal: z.number().nullable(),
  /**
   * How fresh this period's stat lines are, and since when (BACKLOG L2,
   * N5): \`none\` before any game has kicked off, \`delayed\` while one that has
   * kicked off has no lines, \`final\` once every one that has kicked off has
   * them, \`live\` never until a live feed exists. A 0 on the board beside
   * \`delayed\` is a wait, not a score. Added 2026-09-25.
   */
  stats: StatsFreshness,
  bestBall: z.boolean(),
  /**
   * The week this team went out of an elimination league (Q1, B-364): its
   * roster went to waivers, and it has no lineup to set. Null while alive,
   * and in any other format. Added 2026-10-16.
   */
  eliminatedPeriod: z.number().int().nullable().default(null),
  updatedAt: Iso,
});
export type LineupView = z.infer<typeof LineupView>;

export const LineupAssignment = z.object({
  period: z.number().int(),
  rows: z.array(
    z.object({ slot: z.string(), slotIndex: z.number().int(), playerId: z.string().nullable() }),
  ),
});
export type LineupAssignment = z.infer<typeof LineupAssignment>;

// ── Matchup ───────────────────────────────────────────────────────────────

export const MatchupRowView = z.object({
  slot: z.string(),
  player: PlayerSummary.nullable(),
  game: GameStatus.nullable(),
  points: z.number().nullable(),
  /**
   * NULL MEANS NOT PROJECTED, and it is not the same as zero.
   *
   * The simulator has a projection for everybody, so this was never nullable
   * and never needed to be. A server does not: `player_projections` had no
   * writer until Epic F (F1, 2026-09-25), and a player with no NFL games has
   * no projection (`basis.withheld` says so) — and a zero here renders as
   * "projected to score nothing", a claim about a player, in the field a
   * lineup decision is made on. A player who cannot play IS projected at 0,
   * and `basis.adjustment` says why (B-165). Made nullable by E1, 2026-09-19.
   */
  projection: z.number().nullable(),
  statLine: z.string().nullable(),
  /** How `points` was made, stat by stat: `LineupRowView.breakdown`, from the same read. */
  breakdown: z.array(ScoreItem).nullable().default(null),
  /** The most recent scoring change, for the flash and the +6.0 chip. */
  lastDelta: z.object({ points: z.number(), label: z.string(), at: Iso }).nullable(),
  /** Where the projection came from. Null on an empty slot. */
  basis: ProjectionBasis.nullable(),
  /**
   * Why this starter scores nothing, when the reason is plain (N1, B-233):
   * `empty`, nobody in the slot; `bye`, his team has no game this week;
   * `did-not-play`, his game is over and its lines have landed with none of
   * his. Each counts 0 in the score, as on ESPN, Yahoo and Sleeper. Null for a
   * starter who can still score or did, and while a finished game's lines are
   * late — a wait, not a zero (B-172). `noScoreOf` decides it. Added 2026-10-02.
   */
  noScore: z.enum(['empty', 'bye', 'did-not-play']).nullable().default(null),
  /**
   * A starter listed with a status that rules him out, in a week that has
   * begun, whose game is still to come: not yet to play (`MatchupSide.yetToPlay`
   * leaves him out), and listed with those not playing rather than those to
   * come. Added 2026-10-26 (B-521).
   */
  ruledOut: z.boolean().default(false),
});
export type MatchupRowView = z.infer<typeof MatchupRowView>;

export const MatchupSide = z.object({
  team: TeamRef,
  score: z.number(),
  /**
   * Points so far plus what is still to come (`sideTotal`, B-166). Null while
   * a starter still to play has no projection: the sum of a list with a hole
   * in it is not a number.
   */
  projected: z.number().nullable(),
  yetToPlay: z.number().int(),
  inProgress: z.number().int(),
  rows: z.array(MatchupRowView),
  /** Null until a bench line has scored. A bench of nulls is not zero (E4). */
  benchPoints: z.number().nullable(),
  /**
   * The bench, a row each in the lineup's order, an open spot included: each
   * with his game, points and projection as a starter's row has them. In best
   * ball, everybody whose points did not count. Not IR or a taxi squad.
   * Empty when the lineup cannot be read. `benchPoints` is their total.
   * Added 2026-10-17 (the bench on Game Day, MATCHUP.md).
   */
  bench: z.array(MatchupRowView).default([]),
  /**
   * A commissioner's adjustments to this team's points this week, each in the
   * score already (COMMISSIONER_OVERRIDES.md, J1): why the score is not the
   * rows' sum. Empty almost always. Added 2026-09-30.
   */
  adjustments: z.array(z.object({ points: z.number(), reason: z.string(), at: Iso })).default([]),
  /**
   * What this side's bot says about the week's game (AI_MANAGERS.md §6.5,
   * B-458): its name and one line, before the games, while they are played,
   * or after. Null for a person's side, a quiet week, a bot seated after the
   * week's first kickoff, a playoff game's final, and a reader who muted
   * the bots. The same line for every reader who sees one. Added 2026-10-19.
   */
  botLine: z.object({ name: z.string(), text: z.string() }).nullable().default(null),
});
export type MatchupSide = z.infer<typeof MatchupSide>;

export const PlayView = z.object({
  id: z.string(),
  at: Iso,
  playerId: z.string(),
  playerName: z.string(),
  side: z.enum(['home', 'away']),
  description: z.string(),
  points: z.number(),
});
export type PlayView = z.infer<typeof PlayView>;

/** The series between two teams, from the home team's side, with the line the card shows. */
export const RivalryView = z.object({
  wins: z.number().int(),
  losses: z.number().int(),
  ties: z.number().int(),
  games: z.number().int(),
  leaderTeamId: z.string().nullable(),
  /** The team on the streak and how many; 0 when there is none worth saying. */
  streakTeamId: z.string().nullable(),
  streak: z.number().int(),
  /** "Dave leads 7–4; you've lost 4 straight" or "First meeting." */
  line: z.string(),
});
export type RivalryView = z.infer<typeof RivalryView>;

export const MatchupSummary = z.object({
  id: z.string(),
  period: z.number().int(),
  /**
   * Where the matchup stands at the instant it is read: `pre` before any
   * game in its period has kicked off, `live` from the first kickoff until
   * it is settled, `final` once it is. A reading, not the stored row's
   * status alone: a week whose first game has kicked off is under way before
   * a scoring pass has touched it (round 3's review, NUMBERS-6). `live` is
   * the week being played, not live numbers — the period's `stats` says
   * how fresh those are.
   */
  status: z.enum(['pre', 'live', 'final']),
  home: z.object({
    team: TeamRef,
    score: z.number(),
    projected: z.number().nullable(),
    yetToPlay: z.number().int(),
  }),
  away: z.object({
    team: TeamRef,
    score: z.number(),
    projected: z.number().nullable(),
    yetToPlay: z.number().int(),
  }),
  /**
   * Null means nobody has worked it out.
   *
   * A win probability is a function of the two projected totals
   * (`winProbabilityHome` in this package, B-166), and null when either is.
   * Exactly 1 or 0 only when no starter on either side has a game still to
   * come; while anybody has one it stays between 0.01 and 0.99, so a whole
   * percent never reads 100 or 0 before the last game. 0.5 whenever the two
   * totals are level. A `final` matchup's is its result — 1, 0, or 0.5 for
   * level scores — and its `projected` totals are its scores: a settled
   * week has nothing still to come, whatever lines landed after it was
   * settled (NUMBERS-5).
   * The number this used to be forced to carry was 0.5 — which a screen draws
   * as an evenly split bar, indistinguishable from a genuinely even matchup.
   * Made nullable by E1, 2026-09-19.
   */
  winProbabilityHome: z.number().min(0).max(1).nullable(),
  liveLabel: z.string().nullable(),
  kind: z.enum(['regular', 'median', 'playoff', 'consolation']),
  /** All-time head-to-head and the current streak; the game being shown counts once it is final. */
  rivalry: RivalryView.nullable(),
  /**
   * The team a final went to, by its score or, on a level score, by the
   * league's tiebreaker (`decidedBy` on the matchup says which). Null for a
   * tie, and before the matchup is final: a team ahead on Sunday has not won
   * anything. Read this rather than comparing the scores, which cannot say
   * who won a tie the league broke. Added 2026-10-02 (N1).
   */
  winnerTeamId: z.string().nullable().default(null),
  /**
   * The week's second game, in a league that plays two opponents a week
   * (scenario Q6): each team's one score set against a second team. False
   * for a week's only game and for its first. Added 2026-11-01.
   */
  secondGame: z.boolean().default(false),
});
export type MatchupSummary = z.infer<typeof MatchupSummary>;

export const MatchupView = MatchupSummary.extend({
  home: MatchupSide,
  away: MatchupSide,
  plays: z.array(PlayView),
  lastUpdatedAt: Iso,
  /**
   * How fresh this period's stat lines are, and since when (BACKLOG L2,
   * N5): \`none\` before any game has kicked off, \`delayed\` while one that has
   * kicked off has no lines, \`final\` once every one that has kicked off has
   * them, \`live\` never until a live feed exists. A 0 on the board beside
   * \`delayed\` is a wait, not a score. Added 2026-09-25.
   */
  stats: StatsFreshness,
  /**
   * The rule that settled a level final (N1, B-232): `higher-seed` for a
   * bracket game, else the league's `tiebreakers.matchup` — `bench-points`,
   * `home-team` or `coin-flip`. Null for every other matchup, including a
   * level final that stayed a tie (the league's rule is `tie`, or the benches
   * were level too). `winnerTeamId` says which side it went to; each side's
   * `benchPoints` is what a bench-points tiebreak compared.
   */
  decidedBy: z.string().nullable(),
  /**
   * How this matchup is settled if it ends level, under the rules it is
   * scored by (`matchupTiebreak`): `higher-seed` for a bracket game, else the
   * league's `tiebreakers.matchup`. Lets a client say why a level final stayed
   * a tie — the league keeps ties (`tie`), or the benches were level too
   * (`bench-points`). Added 2026-10-02 (N1).
   */
  tieRule: z.enum(['tie', 'bench-points', 'home-team', 'coin-flip', 'higher-seed']).default('tie'),
  margin: z.number().nullable(),
  /**
   * The week's game against the league median (scenario Q3), for a
   * head-to-head week that plays one: the median of every team's score as
   * the last scoring pass wrote it, and where each side stands against it —
   * a win above, a loss below, a tie on it (`againstMedian`). So far while the
   * matchup is under way, the result once it is final. Null for a week that
   * plays no median, and before a scoring pass has written one. Added
   * 2026-10-16.
   */
  median: z
    .object({
      score: z.number(),
      home: z.enum(['above', 'below', 'level']),
      away: z.enum(['above', 'below', 'level']),
    })
    .nullable()
    .default(null),
});
export type MatchupView = z.infer<typeof MatchupView>;

/**
 * One team's week in a league played against the field (scenarios Q4 and Q5,
 * PLAN-2026-10-16-league-formats): all-play, total points and elimination
 * have no opponents, so a week is a team's score against everybody's. Its
 * place and its week record are `fieldResults` over the week's scores as the
 * last scoring pass wrote them. Added 2026-10-16.
 */
export const FieldRowView = z.object({
  /** The team's matchup row this week: a regular-season row with nobody away. */
  id: z.string(),
  period: z.number().int(),
  team: TeamRef,
  score: z.number(),
  /** Points so far plus what is still to come (`sideTotal`); its score once final. Null when not worked out. */
  projected: z.number().nullable(),
  yetToPlay: z.number().int(),
  /** Read as a matchup's is (`MatchupSummary.status`). */
  status: z.enum(['pre', 'live', 'final']),
  /** 1 for the week's top score; teams level share a place. */
  place: z.number().int(),
  /** The week against every other team: a win for each lower score, a loss for each higher, a tie for each level one. */
  week: z.object({ wins: z.number().int(), losses: z.number().int(), ties: z.number().int() }),
  /**
   * In an elimination league (scenario Q1, B-364), where the team stands
   * against the week's cut: `out`, knocked out this week once it is settled;
   * `danger`, among the lowest scores while the week is played; `safe`
   * otherwise. Null before the scores tell teams apart, and in any other
   * format. Added 2026-10-16.
   */
  cut: z.enum(['safe', 'danger', 'out']).nullable().default(null),
});
export type FieldRowView = z.infer<typeof FieldRowView>;

export const ScoreboardView = z.object({
  period: PeriodRef,
  matchups: z.array(MatchupSummary),
  /**
   * The week's league median, for a week that plays a game against it
   * (scenario Q3): the median of every team's score as the last scoring pass
   * wrote it. Null for a week that plays none, and before a pass has written
   * one. Added 2026-10-16.
   */
  median: z.number().nullable().default(null),
  /**
   * The week in a league played against the field: every team's week in
   * order of place, mine among them, and `matchups` empty. Null in a
   * head-to-head league. Added 2026-10-16.
   */
  field: z.array(FieldRowView).nullable().default(null),
  /**
   * In an elimination league (Q1), every team out by the end of this week,
   * with the week it went out, latest first. Null in any other format.
   * Added 2026-10-16.
   */
  out: z
    .array(z.object({ team: TeamRef, period: z.number().int() }))
    .nullable()
    .default(null),
  /** When this answer was made: the request's instant, not the numbers' (that is \`stats.asOf\`). */
  lastUpdatedAt: Iso,
  /**
   * How fresh this period's stat lines are, and since when (BACKLOG L2,
   * N5): \`none\` before any game has kicked off, \`delayed\` while one that has
   * kicked off has no lines, \`final\` once every one that has kicked off has
   * them, \`live\` never until a live feed exists. A 0 on the board beside
   * \`delayed\` is a wait, not a score. Added 2026-09-25.
   */
  stats: StatsFreshness,
});
export type ScoreboardView = z.infer<typeof ScoreboardView>;

// ── Standings ─────────────────────────────────────────────────────────────

export const StandingsRow = z.object({
  /** Null until a period has been finalized: a table nobody has played is not ranked (E6). */
  rank: z.number().int().nullable(),
  team: TeamRef,
  wins: z.number(),
  losses: z.number(),
  ties: z.number(),
  pointsFor: z.number(),
  pointsAgainst: z.number(),
  /** 'W3', 'L1'. Null before anybody has played — a team with no games is not on a streak. */
  streak: z.string().nullable(),
  /**
   * Games behind the best record in the table: half the gap in wins less
   * losses, a tie counting half each way (`withGamesBack`). 0 for the leader
   * and every team level with it; null for every row before anybody has
   * played. Added 2026-10-12.
   */
  gamesBack: z.number().nullable().default(null),
  /**
   * Points behind the most points in the table: the race in a total-points
   * league (scenario Q5), 0 for the leader. Null for every row before anybody
   * has played. Added 2026-10-16.
   */
  pointsBack: z.number().nullable().default(null),
  /** The week the team went out of an elimination league (Q1); null while alive, and in any other format. */
  eliminatedPeriod: z.number().int().nullable().default(null),
  /**
   * Clinched a playoff seat, out of the race, or neither yet (`playoffStatuses`,
   * B-397): certain, never likely, from the records and the regular-season
   * games each team has left (every regular game not yet final), by the
   * share of games won as the table ranks, the tiebreakers not run forward;
   * once every regular-season game is final, the field as the table has it.
   * Null where it is not measured — no seats or a seat for everybody, a race
   * on points or all-play — before the table is written, and where a store does not work it out (the reference store
   * keeps no schedule, the practice league plays a week).
   * Written by the standings since 2026-10-17; in a league seeded division
   * winners first since 2026-10-20, by the seeds (B-463).
   */
  playoffStatus: z.enum(['clinched', 'alive', 'eliminated']).nullable(),
  divisionId: z.string().nullable(),
  tiebreakNote: z.string().nullable(),
  /**
   * What has to happen, in the regular season's last week (H1, B-460): the
   * routes that are enough to put this team in — "In with a win and a loss
   * by Pylon Pushers. Out with a loss." Null for a team in or out whatever
   * happens, in every other week, and where the race is not searched (a race
   * on points, all-play, a median league of more than twelve teams). Added
   * 2026-10-19; a median league and one seeded division winners first since
   * 2026-10-20 (B-463).
   */
  raceNote: z.string().nullable().default(null),
  /**
   * The playoff seed the team would hold if the regular season ended now, by
   * the league's seeding rule (`withSeeds`); null outside the field, and for
   * every row while the table is unranked. Under division-winners-first a
   * division winner ranked below the line is seeded and a better record
   * above it may not be, so a client marks the field by this, not by rank.
   * Added 2026-10-17.
   */
  seed: z.number().int().min(1).nullable().default(null),
  /**
   * Each regular-season game against somebody, oldest first: 'W', 'L' or
   * 'T' by the stored winner, as the streak counts them (`tableLines`). A
   * median game and a week against the field are not in it. Added 2026-10-17.
   */
  results: z.array(z.enum(['W', 'L', 'T'])).default([]),
  /**
   * The all-play record: each finished regular week's score against every
   * other team's that week (`tableLines`), the record an all-play league is
   * ranked by. Null before the team has a finished week. Added 2026-10-17.
   */
  allPlay: z
    .object({ wins: z.number().int(), losses: z.number().int(), ties: z.number().int() })
    .nullable()
    .default(null),
  /**
   * Games from the playoff line (`withPlayoffLine`): for a team in the field,
   * how many games clear it is of the first team out; for a team outside,
   * negative, how many short of the last team in; zero level with the team
   * across the line. Under division winners first the race is for the seats
   * the leaders leave, and a leader is null. Null for every row while the
   * table is unranked, has no line, or has no record to measure it in (a
   * race on points). Added 2026-10-17.
   */
  gamesFromLine: z.number().nullable().default(null),
  /**
   * Max PF (B-419): the points the team could have scored over the same
   * weeks as `pointsFor` had it started its best lineup each week from the
   * players it had — the best lineup each settled week's facts recorded
   * (`team_periods`, the awards' *left on the bench*), or the score where
   * that is higher (a commissioner's adjustment), the score where the two
   * are the same lineup (the best is kept to the tenth, a score to the
   * thousandth). Sleeper's *Max PF*, ESPN's and Yahoo's *potential points*.
   * Null while a counted week has no facts written, before the first, and
   * on Home's `standingsWindow`. Added 2026-10-18.
   */
  maxPoints: z.number().nullable().default(null),
});
export type StandingsRow = z.infer<typeof StandingsRow>;

export const StandingsView = z.object({
  /** Null until a period has been finalized; never 0, which would name a week (U10). */
  throughPeriod: z.number().int().nullable(),
  playoffTeamCount: z.number().int(),
  /** The regular season's last week: the weeks still to play are the ones after `throughPeriod`. Added 2026-10-17. */
  regularSeasonEnd: z.number().int().nullable().default(null),
  rows: z.array(StandingsRow),
  divisions: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type StandingsView = z.infer<typeof StandingsView>;

// ── Players ───────────────────────────────────────────────────────────────

export const Availability = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('free-agent') }),
  /**
   * On waivers until `clearsAt`: after a drop or the draft, or, for a free
   * agent whose game has started, until the first run after the period
   * (PLAN-2026-09-24 §2 rule 6, B-142), claimable and not addable. `myClaimId`
   * is my pending claim on this player, if any.
   */
  z.object({ kind: z.literal('waivers'), clearsAt: Iso, myClaimId: z.string().nullable() }),
  z.object({ kind: z.literal('owned'), team: TeamRef }),
  z.object({ kind: z.literal('mine'), slot: z.string() }),
]);

export type Availability = z.infer<typeof Availability>;

/**
 * What a row or a card offers for this player: add a free agent, claim one on
 * waivers, drop one of mine, trade for another team's. `none` when nothing can
 * be done now, with the row's `reason` saying why.
 */
export const PlayerAction = z.enum(['add', 'claim', 'drop', 'trade', 'none']);
export type PlayerAction = z.infer<typeof PlayerAction>;

/**
 * The deeper numbers (SCENARIOS E8, PLAN-2026-10-03-research-tools.md §11),
 * a client's to show at the Everything level: each a figure with its own
 * name, never a label that tells anybody what to do — no *boom*, no *bust*.
 * Each is null where there is nothing to measure yet.
 */
export const DeeperNumbers = z.object({
  /**
   * Points a game over his last three weeks with a line, less over the
   * season, in this league's scoring: +3.2 is three points a game above his
   * season. Null with fewer than four weeks, where the two are the same weeks.
   */
  form: z.number().nullable(),
  /** His lowest and highest week, and how many of his weeks reached twice his average. */
  range: z
    .object({
      low: z.number(),
      high: z.number(),
      twiceAverage: z.number().int().min(0),
      weeks: z.number().int().min(1),
    })
    .nullable(),
  // NO SCHEDULE AVERAGE since 2026-10-26 (B-519): the rest of the season's
  // matchup ranks averaged drift to the middle and said little. A week's
  // matchup is the row's own (`PlayerRowView.matchup`).
  /**
   * His usage, in the stats the sport names (`SportSummary.usage`): his share
   * of his club's total of the `share` stat over the season's games — the
   * club his line was filed for — and over the last three of them (0 to 1),
   * and the `perGame` stat a game over those games, null where no line of the
   * season measures it. Null for a sport with no usage, or a player with none
   * of the `share` stat in a game whose club is known. Added 2026-10-03.
   */
  usage: z
    .object({
      share: z.number().min(0).max(1),
      shareLastThree: z.number().min(0).max(1),
      perGame: z.number().nullable(),
      games: z.number().int().min(1),
    })
    .nullable()
    .default(null),
  /**
   * His last weeks with a line, at most `RECENT_WEEKS`, oldest first, in this
   * league's scoring: the points behind his form, for a client to draw as a
   * line (B-410). A bye or a week with no line is not in it — these are his
   * games, not the calendar. Empty with none. Added 2026-10-17; absent is
   * empty, so a client written before it reads as it did.
   */
  recent: z.array(z.number()).max(5).default([]),
});
export type DeeperNumbers = z.infer<typeof DeeperNumbers>;

/** How many of his last weeks `DeeperNumbers.recent` holds. */
export const RECENT_WEEKS = 5;

/**
 * The games a pool read's stats are over (`PlayersQuery.stats`, B-412):
 * `season` his games this season; `recent` his last three games with a
 * line, the games behind `lastThreeAvg`; `week` the league's last week that
 * has ended, which `PlayersPage.statWeek` names; `projected` this week's
 * projection, its stat line. Added 2026-10-18.
 */
export const StatRange = z.enum(['season', 'recent', 'week', 'projected']);
export type StatRange = z.infer<typeof StatRange>;

/** How many games `recent` is over: the last three, `lastThreeAvg`'s. */
export const RECENT_GAMES = 3;

/**
 * One column of a position's table (B-517): a stat, or a figure the sport
 * makes from stats — yards a carry, completions of attempts, points allowed a
 * game, field goals made of tried, a share of his club's targets — labeled
 * and named for a reader. `level` is the least detail level it shows at
 * (DETAIL_LEVELS.md): a client shows the `simple` ones alone, adds the
 * `standard` ones, and the `everything` ones last; the server sends them all.
 * `decimals` is the places its numbers are shown to. A made-of column holds
 * the made under `key` and the tries under `attempts` in the same values, to
 * read *18/21*; null for every other. `perGame` says its numbers are a
 * game's average (B-520): a count over the season or the last three divided
 * by his weeks with a line in it, as `RowStatsView.average` is (a game each
 * in football), its `name` saying *a game*; a rate, a share or a figure that
 * is already a game's is not. `casual` is
 * `level` `simple`, kept for a client written before `level`. Added
 * 2026-10-18 (B-412); `level`, `decimals`, `attempts` and `perGame`
 * 2026-10-26.
 */
export const StatColumnView = z.object({
  key: z.string(),
  label: z.string(),
  name: z.string(),
  casual: z.boolean(),
  level: z.enum(['simple', 'standard', 'everything']).default('standard'),
  decimals: z.number().int().min(0).max(3).default(0),
  attempts: z.string().nullable().default(null),
  perGame: z.boolean().default(false),
});
export type StatColumnView = z.infer<typeof StatColumnView>;

/**
 * A stat table: which positions it is for, and its columns in the sport's
 * order: their stat sheets' (B-517), joined for a slot that takes several.
 */
export const StatGroupView = z.object({
  positions: z.array(z.string()),
  columns: z.array(StatColumnView),
});
export type StatGroupView = z.infer<typeof StatGroupView>;

/**
 * A choice of positions a list of players offers (B-511): one position, or a
 * slot of the league's that takes several (*Flex*). `id` is the position's
 * or the slot's; `label` how it reads (`DST` reads *DEF*); `name` what it is,
 * a slot's with who fits it (*Flex: RB, WR or TE*); `positions` what to ask
 * for. Added 2026-10-26.
 */
export const PositionFilterView = z.object({
  id: z.string(),
  label: z.string(),
  name: z.string(),
  positions: z.array(z.string()).min(1),
});
export type PositionFilterView = z.infer<typeof PositionFilterView>;

/**
 * A row's numbers under the range asked (B-412). `games` is how many of his
 * games the range holds — for `projected`, 1 when he is projected and 0 when
 * not. `values` holds his numbers in the columns of the table he is read in
 * once `games` is above zero (`StatColumnView`): a zero where his lines have
 * none, except where there is nothing to say — a stat the league scores in
 * tiers (points allowed) that no game of the range reported, since a 0 there
 * reads as a shutout; a rate over nothing (yards a carry with no carries); a
 * share of his club's total for a week or a projection, which no table keeps
 * — which are left out; nothing at all with no games. `points` is the range's
 * fantasy points in this league's scoring and `average` those a game, null
 * with no games. `average` added 2026-10-26.
 */
export const RowStatsView = z.object({
  games: z.number().int().min(0),
  values: z.record(z.string(), z.number()),
  points: z.number().nullable(),
  average: z.number().nullable().default(null),
});
export type RowStatsView = z.infer<typeof RowStatsView>;

/**
 * One player in a league's pool.
 *
 * FIVE FIGURES ARE NULLABLE, AND NULL IS NOT ZERO. The simulator makes up every
 * one of them; a server does not have them all. `projection` is null for a
 * player nobody projected (the lineup's precedent, `LineupRowView.projection`);
 * `ownershipPct` and `trend` are site-wide counts from the site's own leagues
 * (`site_trends`, B-251), null where none is kept for the player; and
 * `seasonPoints` and `lastThreeAvg` are the league's scoring over stored lines,
 * which a server works out rather than invents. A client renders null as the
 * dash (`num()` in the web client), never as `0.0`, `0%` or a flat arrow —
 * each of those is a claim about the player. Made nullable by W2 of
 * PLAN-2026-09-24-free-agents-and-waivers.
 */
export const PlayerRowView = z.object({
  player: PlayerSummary,
  game: GameStatus.nullable(),
  /** The matchup cue for this period's game (`LineupRowView.matchup`). Added 2026-10-03. */
  matchup: MatchupCue.nullable().default(null),
  /**
   * This period, in the league's scoring. Null when nobody projected him for
   * it — `basis.withheld` says why when there is a reason to say.
   */
  projection: z.number().nullable(),
  /**
   * Where the projection came from, the lineup's shape (`LineupRowView.basis`):
   * `seasonAvg` is `seasonPoints` over the games that produced it, so the
   * pool and the lineup never disagree. Added 2026-09-25 (N3).
   */
  basis: ProjectionBasis.nullable(),
  /** This season, in the league's scoring. */
  seasonPoints: z.number().nullable(),
  /**
   * Points a game over the player's last three games with a line — over the
   * ones there are when fewer have been played, and null before the first.
   * Not the season's average: a player's recent form is the point of it.
   */
  lastThreeAvg: z.number().nullable(),
  /**
   * Points a game this season: `seasonPoints` over the weeks that produced
   * it, the lineup's AVG. Null before his first. Added 2026-10-14.
   */
  seasonAvg: z.number().nullable().default(null),
  /** Of the leagues on the site, the share that roster the player: 0 to 100. */
  ownershipPct: z.number().nullable(),
  /**
   * Which way he is moving across the site's leagues: the sign of `adds`.
   * Null when nobody measured it; `flat` when they did and nothing moved.
   */
  trend: z.enum(['up', 'down', 'flat']).nullable(),
  /**
   * Net adds over the last seven days across the site's leagues of this
   * sport — adds that took him in, less drops that let him go — or null
   * with no leagues to measure in. Added 2026-10-03 (B-251).
   */
  adds: z.number().int().nullable().default(null),
  /**
   * His rank at his first position by this week's projection in this
   * league's scoring, among every player of the league's world projected
   * there: 12 for the twelfth-highest projected running back. Null with no
   * projection. Added 2026-10-03 (B-251).
   */
  posRank: z.number().int().min(1).nullable().default(null),
  /**
   * His rank at his first position by season points in this league's
   * scoring, among every player of the league's world who has scored there:
   * 9 for the ninth-most points by a wide receiver. Ties share a rank. Null
   * before he has scored. The big sites' "WR9". Added 2026-10-14 (B-321).
   */
  seasonRank: z.number().int().min(1).nullable().default(null),
  /** On my watchlist (`WatchlistView`, E5). False for nobody signed in. Added 2026-10-03. */
  watched: z.boolean().default(false),
  /** The deeper numbers (E8), for the Everything level. Added 2026-10-03. */
  deeper: DeeperNumbers.nullable().default(null),
  /**
   * His stats under `PlayersQuery.stats`, or null when the read asked for
   * none. Added 2026-10-18 (B-412).
   */
  stats: RowStatsView.nullable().default(null),
  availability: Availability,
  locked: z.boolean(),
  action: PlayerAction,
  reason: z.string().nullable(),
});
export type PlayerRowView = z.infer<typeof PlayerRowView>;

/**
 * `Page<PlayerRowView>`: one page of the pool, and how many match.
 *
 * `rows` BECAME `items` in W2 (2026-09-24), the last of the three paging shapes
 * `pageOf` was written to end; the other two moved on 2026-09-17. And the
 * **"for you" list left the served shape**: it was the simulator's pick of
 * a few free agents with a sentence each, which no server can compute and which
 * DETAIL_LEVELS.md and PLAYERS.md respecified as *Worth a look* — a read of its
 * own (`GET /leagues/:id/teams/:teamId/suggestions`, P1, not designed). The
 * simulator drew its strip outside the contract (`pickupStrip`) until the
 * Players screen stopped drawing one on 2026-10-24 (B-487).
 */
/** An order the pool can be read in. */
export const PlayerSort = z.enum([
  'projection',
  'points',
  // Points a game this season (`seasonAvg`) and over the last three games
  // (`lastThreeAvg`). Added 2026-10-14 (B-320).
  'average',
  'recent',
  'trend',
  'ownership',
  'name',
  // The deeper numbers (E8, `DeeperNumbers`), the Everything level's columns:
  // form, the highest week (the range's top) and the usage share. Added
  // 2026-10-17 (B-377); the schedule's average went on 2026-10-26 (B-519),
  // and the lowest week (the range's bottom) came (B-520).
  'form',
  'range',
  'low',
  'usage',
  // One stat under the range asked, `PlayersQuery.stat`, or with none named
  // the range's fantasy points (B-412). Added 2026-10-18.
  'stat',
  // His place on his club's depth chart: by its column A to Z, each from its
  // first string down (B-479). Added 2026-10-23.
  'depth',
]);
export type PlayerSort = z.infer<typeof PlayerSort>;

export const PlayersPage = pageOf(PlayerRowView).extend({
  /** How many match the query, ignoring the page. Always counted here. */
  total: z.number().int().min(0),
  /**
   * The positions this league's roster can start, in the sport's order: the
   * filter chips a client offers (QB … DST, or DL, LB and DB in a league of
   * defensive players). Added 2026-10-18 (Q11).
   */
  positions: z.array(z.string()).default([]),
  /**
   * The pro clubs of the league's world, by the id the rows' `proTeam` spells
   * — the only ids `club` matches — each named as its club unit is, A to Z by
   * name: the choice a client offers. A club's id is the data's own, which
   * need not be the sport's list's (nflverse's Rams are `LA`). Added
   * 2026-10-22 (E1, B-479).
   */
  clubs: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  /**
   * The order these rows are in: the one asked for, or the one the server
   * picked when none was (`PlayersQuery.sort`), so a client can say which
   * figure the list is sorted by. Added 2026-09-25 (N3).
   */
  sort: PlayerSort,
  /**
   * The slot asked about (`PlayersQuery.slot`), as this league's roster has
   * it: its name, its kind and the positions it takes — none named at the
   * bench and the reserve, where the rule is anyone and a status — so a
   * client can say what the list is without knowing the sport. Null with no
   * slot asked. Added 2026-10-14 (B-320).
   */
  slot: z
    .object({
      id: z.string(),
      name: z.string(),
      kind: z.enum(['starter', 'bench', 'reserve', 'taxi']),
      positions: z.array(z.string()),
    })
    .nullable()
    .default(null),
  /**
   * The stat the rows are in order by when `sort` is `stat`, null for the
   * range's fantasy points and for every other sort. Added 2026-10-18.
   */
  stat: z.string().nullable().default(null),
  /**
   * What a client offers to narrow the pool to (B-511): each position the
   * league's starting slots take and each slot that takes several, in the
   * sport's order, a slot after the last of its positions. One at a time;
   * a filter's `positions` is what to send as `PlayersQuery.positions`.
   * Added 2026-10-26.
   */
  filters: z.array(PositionFilterView).default([]),
  /**
   * With `PlayersQuery.stats`, the tables the rows are read in (B-517): with
   * `positions` asked, one over them — their stat sheets', whatever their
   * numbers; with a `team` asked, one for each position its roster holds, in
   * the order of `filters`; with neither, none, since no position's columns
   * fit every row, and a client shows the fantasy figures alone. Empty when
   * no range was asked. Added 2026-10-18 (B-412); by position since
   * 2026-10-26, by position group before.
   */
  statGroups: z.array(StatGroupView).default([]),
  /**
   * The week `stats=week` read: the league's last week that has ended. Null
   * for any other range, and before a week has ended. Added 2026-10-18.
   */
  statWeek: z
    .object({ period: z.number().int().min(1), label: z.string() })
    .nullable()
    .default(null),
});
export type PlayersPage = z.infer<typeof PlayersPage>;

/**
 * `GET /leagues/:id/players?…` — which players, in what order.
 *
 * THE DEFAULT SORT IS ONE A SERVER CAN HONOR. It was `projection`, and every
 * projection was null until Epic F, so the default order of a real league's
 * pool would have been no order at all; it became `points`, and from N3
 * (2026-09-25) the server picked `projection` for a week that had them. A
 * week's projection is nothing for a player on his bye, so a manager's
 * look for a pickup lost every one of them to the last page; since
 * 2026-10-17 the default is `average`, points a game this season, the
 * figure a pickup is weighed by (B-365). Every sort is highest first except
 * `name`, and `reverse` turns it round; a row whose figure is null sorts
 * after every row that has one either way, and ties go by name, so a sort
 * over a figure nobody has computed is a list by name rather than a refusal.
 *
 * `watch` IS GONE from `availability`: there is no watchlist anywhere to read
 * (SCENARIOS E5, not built). A caller that sends it is refused, not answered
 * with an empty list that reads as "you are watching nobody".
 */
export const PlayersQuery = z.object({
  /**
   * Whose players: `free-agents` is everybody a team may add or claim, those
   * on waivers among them; `waivers` only those; `taken` everybody on
   * another team's roster (added 2026-10-17); `watching` the players on my
   * watchlist, whoever holds them (E5, added 2026-10-03).
   */
  availability: z
    .enum(['free-agents', 'waivers', 'taken', 'all', 'mine', 'watching'])
    .default('free-agents'),
  /** Capped, as `DirectoryQuery.positions` is: a sport has a handful. */
  positions: z.array(z.string()).max(32).default([]),
  /**
   * A slot of this league's roster, by id: only the players who can fill it
   * (`canFillSlot`) — a starting slot's positions (a FLEX's three), every
   * position at the bench, and at the reserve only the players whose status
   * qualifies. `positions` narrows it further. A slot the roster does not
   * have is refused. Added 2026-10-14 (B-320).
   */
  slot: z.string().min(1).max(32).optional(),
  /**
   * One team's roster, by its id: only the players it holds, whatever
   * `availability` says — another team's page's Stats view reads its players
   * so (D1, B-461). A team the league does not have answers an empty page.
   * Added 2026-10-19.
   */
  team: z.string().min(1).max(80).optional(),
  /**
   * One pro club's players, by its id (`ProTeamRef.id`, what each row's
   * `proTeam` says; `GET /api/sports` lists the sport's): its roster and its
   * depth chart side by side, with `availability` as ever. A club with nobody
   * in the pool answers an empty page. Added 2026-10-22 (E1).
   */
  club: z.string().min(1).max(8).optional(),
  q: z.string().default(''),
  /**
   * Absent, `average` (B-365). The page's `sort` says which order it is in.
   * With `stats` asked, `points` and `average` are the range's
   * (`RowStatsView.points`, `.average`), the figures the list shows beside
   * its stats; without, the season's (B-518, 2026-10-26).
   */
  sort: PlayerSort.optional(),
  /**
   * The order turned round: lowest first, or Z to A by name. A row whose
   * figure is null still comes after every row that has one. Added
   * 2026-10-17 (B-366).
   */
  reverse: z.boolean().default(false),
  /**
   * Each row's stats over these games (`StatRange`), and the page's tables
   * for them (`PlayersPage.statGroups`). Absent, the rows carry none.
   * Added 2026-10-18 (B-412).
   */
  stats: StatRange.optional(),
  /**
   * The column key `sort=stat` orders by, a column of one of the sport's
   * positions' tables (`StatColumnView.key`: a stat, or a figure such as
   * yards a carry); absent, `sort=stat` orders by the range's fantasy points
   * (`RowStatsView.points`). `sort=stat` is refused without `stats`, and
   * `stat` without `sort=stat` or for a key no position's table has. A key
   * the page's tables do not draw orders by the range's points, and the
   * page's `stat` is null. A row with no number in it sorts after every row
   * with one, as a row with no games does. Added 2026-10-18.
   */
  stat: z.string().min(1).max(32).optional(),
  /**
   * The week each row's game is for; absent is the week a move lands in. The
   * same bounds as every period-scoped route's `?period=` (API.md §3): a
   * whole number from 1 to 60.
   */
  period: z.number().int().min(1).max(60).optional(),
  cursor: z.string().nullable().default(null),
  limit: z.number().int().min(1).max(200).default(50),
});
export type PlayersQuery = z.infer<typeof PlayersQuery>;

// ── Waivers and transactions (WAIVERS.md) ─────────────────────────────────

/**
 * The league's waiver system, as a view carries it. THE RULES' OWN ENUM, not a
 * copy of it: this was a second `z.enum` of the same four words, and a system
 * added to the rules would have failed the view's parse on the day it was used.
 */
export const WaiverSystem = WaiverSettings.shape.system;
export type WaiverSystem = z.infer<typeof WaiverSystem>;

/**
 * How one claim was decided. THE ONE LIST: `packages/waivers` names its
 * outcomes from this type rather than keeping a second union of the same ten
 * words (they agreed until the day one of them grew).
 */
export const ClaimOutcome = z.enum([
  'won',
  'lost-bid',
  'lost-priority',
  'lost-tiebreak',
  'roster-full',
  'over-budget',
  'limit-reached',
  'locked',
  'gone',
  'skipped-conditional',
  // Still pending when the season closed: settled in the batch that completes
  // the league, because no run comes after it (B-152). Never the engine's.
  'season-over',
]);
export type ClaimOutcome = z.infer<typeof ClaimOutcome>;

/** One atomic roster move as the log shows it. */
export const TransactionView = z.object({
  id: z.string(),
  at: Iso,
  kind: z.enum(['add', 'drop', 'claim', 'trade', 'commissioner']),
  team: TeamRef,
  added: z.array(PlayerSummary),
  dropped: z.array(PlayerSummary),
  faab: z.number().nullable(),
  reason: z.string().nullable(),
  /** "Priya added Tyler Allgeier, dropped Roschon Johnson." */
  summary: z.string(),
  reversed: z.boolean(),
  /**
   * Who took it back, when it was: its own manager inside five minutes
   * (`undo`), or the commissioner (`commissioner`, J2). Null while it stands.
   */
  reversal: z.enum(['undo', 'commissioner']).nullable().default(null),
  /** Set while I can still undo this move (5 minutes, nobody else has touched the players). */
  undoableUntil: Iso.nullable(),
  claimId: z.string().nullable(),
  runId: z.string().nullable(),
  tradeId: z.string().nullable(),
});
export type TransactionView = z.infer<typeof TransactionView>;

/** One of my pending claims, in the order I want them tried. */
export const ClaimView = z.object({
  id: z.string(),
  player: PlayerSummary,
  game: GameStatus.nullable(),
  /**
   * The stored projection for the week a move lands in, in this league's
   * scoring: the pool's number for him (round 3). Null when nobody projected him.
   */
  projection: z.number().nullable(),
  bid: z.number().nullable(),
  drops: z.array(PlayerSummary),
  order: z.number().int(),
  conditionalOn: z.object({ claimId: z.string(), playerName: z.string() }).nullable(),
  placedAt: Iso,
  processesAt: Iso.nullable(),
  /**
   * The claim as it stands against my own roster and budget: "Legal. $23 of
   * the $62 left." or the problem. Against the whole budget: a pending claim
   * holds none back (PLAN-2026-09-24 §2 rule 5, B-141).
   */
  standing: z.string(),
  problem: z.string().nullable(),
});
export type ClaimView = z.infer<typeof ClaimView>;

/**
 * A player on waivers (or freshly cleared) as the On waivers list shows it:
 * the players a drop or the draft put there. A free agent whose game has
 * started is on waivers too, in the pool's `Availability`, but not on this
 * list, which on a Sunday afternoon would otherwise be most of the pool (B-142).
 */
export const WaiverPlayerRow = z.object({
  player: PlayerSummary,
  game: GameStatus.nullable(),
  /**
   * The stored projection for the week a move lands in, in this league's
   * scoring: the pool's number for him (round 3). Null when nobody projected him.
   */
  projection: z.number().nullable(),
  /**
   * What the projection rests on, as the Players row's `basis`: above all why
   * it is a stated 0 or none at all (*Out*, *On bye*), which a row says beside
   * it. Added 2026-10-26 (B-521): a waiver row read a bare 0.0 for a player
   * ruled out, where Players said why.
   */
  basis: ProjectionBasis.nullable().default(null),
  /**
   * Points a game this season in this league's scoring, the Players row's
   * `seasonAvg`; null before his first, and in the practice league. Added
   * 2026-10-17 (B-380).
   */
  seasonAvg: z.number().nullable().default(null),
  /** Null until the site counts it; see `PlayerRowView`. */
  ownershipPct: z.number().nullable(),
  clearsAt: Iso,
  cleared: z.boolean(),
  /** Who dropped the player, when known. */
  droppedBy: z.string().nullable(),
  myClaimId: z.string().nullable(),
  action: z.enum(['claim', 'add', 'none']),
  reason: z.string().nullable(),
});
export type WaiverPlayerRow = z.infer<typeof WaiverPlayerRow>;

export const ClaimResultView = z.object({
  claimId: z.string(),
  team: TeamRef,
  player: PlayerSummary,
  drops: z.array(PlayerSummary),
  outcome: ClaimOutcome,
  sentence: z.string(),
  isMine: z.boolean(),
  /**
   * This claim's bid, WHOSEVER IT IS; null outside a budget league.
   *
   * A result exists only once its run is over, and from then every team's bid
   * is shown (B-136, which supersedes B-28's "only yours and the winner's").
   * Before the run a bid is on a pending claim, which only its own team reads.
   */
  bid: z.number().nullable(),
  winningBid: z.number().nullable(),
  winningTeam: TeamRef.nullable(),
  priorityRank: z.number().int().nullable(),
  winnerRank: z.number().int().nullable(),
  /**
   * The claiming team's budget when this claim was decided: after paying, for a
   * win; what there was to spend, for anything else — so an `over-budget`
   * result can say "Bid $23 with $15 left" as a fact and not only in its
   * sentence. Null outside a budget league.
   */
  budgetLeft: z.number().nullable(),
  /** The move a won claim made, which a commissioner can reverse (J2); null for any other outcome. */
  transactionId: z.string().nullable().default(null),
  /** A won claim the commissioner has since reversed (J2). */
  reversed: z.boolean().default(false),
});
export type ClaimResultView = z.infer<typeof ClaimResultView>;

/** One processing run: "Wednesday 3:00 AM · 9 claims · 4 successful". */
export const WaiverRunView = z.object({
  id: z.string(),
  at: Iso,
  claimCount: z.number().int(),
  wonCount: z.number().int(),
  results: z.array(ClaimResultView),
  summary: z.string(),
});
export type WaiverRunView = z.infer<typeof WaiverRunView>;

export const LogEntry = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('run'), at: Iso, run: WaiverRunView }),
  z.object({ kind: z.literal('transaction'), at: Iso, transaction: TransactionView }),
]);
export type LogEntry = z.infer<typeof LogEntry>;

/**
 * Which entries of the log to read: all of them, mine, or one kind. The
 * Waivers screen's chips (`waiversLog.ts` in the web client), as the server
 * answers them: `mine` is my moves and every run with a claim of mine in it,
 * `adds` and `drops` are the moves of that kind (an add that also drops is
 * an add), and `claims` is the runs.
 */
export const LogFilter = z.enum(['all', 'mine', 'adds', 'drops', 'claims']);
export type LogFilter = z.infer<typeof LogFilter>;

/**
 * `GET /leagues/:id/transactions?filter=&cursor=&limit=` — the whole log, a
 * page at a time, newest first (PLAN-2026-09-24 W5). The cursor is bound to
 * the filter it was minted under, the directory's way (`envelope.ts`): one
 * from `mine` replayed against `all` is refused rather than answered with
 * the wrong page.
 */
export const LogQuery = z.object({
  filter: LogFilter.default('all'),
  cursor: z.string().nullable().default(null),
  /** The same cap as the directory and the pool. */
  limit: z.number().int().min(1).max(200).default(50),
});
export type LogQuery = z.infer<typeof LogQuery>;

/** One page of the log. `total` is not counted: the log grows all season and nobody pages to its end. */
export const LogPage = pageOf(LogEntry);
export type LogPage = z.infer<typeof LogPage>;

/**
 * Where a team's waiver priority comes from, as a fact beside the `why` copy
 * (PLAN-2026-09-24 §2 rule 2):
 *
 * - `standings` — reverse standings, recomputed at every run: worst record
 *   first, ties to fewer points scored.
 * - `draft-order` — reverse standings before any game is final, when every
 *   record is 0–0: the reverse of the draft order.
 * - `frozen` — reverse standings once the playoffs start, when the table
 *   stops changing: the order it had at the end of the regular season.
 * - `rolling` — an order that starts at the reverse of the draft and sends
 *   each winner to the back: a rolling league's, and the order a budget
 *   league's tied bids go by when its tiebreak is `waiver-priority` — every
 *   win moves that team back, whatever decided it. `orderRolls` in
 *   `@fsp/waivers` is the one answer both this label and the run read.
 */
export const WaiverPrioritySource = z.enum(['standings', 'draft-order', 'frozen', 'rolling']);
export type WaiverPrioritySource = z.infer<typeof WaiverPrioritySource>;

/** One team in the league's waiver order (J7): its place, and its budget left. */
export const WaiverOrderRow = z.object({
  team: TeamRef,
  /** 1 is first; null when the league has no order (a budget league whose ties go to the earlier claim). */
  rank: z.number().int().min(1).nullable(),
  /** Null outside a budget league. */
  budgetLeft: z.number().int().nullable(),
});
export type WaiverOrderRow = z.infer<typeof WaiverOrderRow>;

/**
 * What a commissioner can change on the Waivers screen now (J7,
 * COMMISSIONER_OVERRIDES.md): a team's place where the order is stored (it
 * rolls), and a budget in a budget league. `orderNote` says why the order
 * cannot be moved when the league has one that is the standings.
 */
export const WaiverCommissionerView = z.object({
  order: z.boolean(),
  budgets: z.boolean(),
  /** The league's budget, the most a team's can be set to; null outside a budget league. */
  budgetMax: z.number().int().nullable(),
  orderNote: z.string().nullable(),
});
export type WaiverCommissionerView = z.infer<typeof WaiverCommissionerView>;

/**
 * The most log entries `WaiversView.log` carries. A season of a quiet league
 * fits; a busy one says `logTruncated`, and `logCursor` reads on from where it
 * stops: `GET /leagues/:id/transactions?filter=all&cursor=` (`Page<LogEntry>`,
 * API.md §3).
 */
export const WAIVERS_LOG_LIMIT = 100;

/**
 * The most players `WaiversView.onWaivers` carries, and it says
 * `onWaiversTruncated` past it. After the draft every undrafted player is on
 * waivers — a couple of thousand in a real league — and one screen's view
 * cannot carry them all; the pool pages them
 * (`GET /leagues/:id/players?availability=waivers`). The list keeps the ones
 * clearing soonest, and among those the most points this season.
 */
export const ON_WAIVERS_LIMIT = 100;

export const WaiversView = z.object({
  league: LeagueRef,
  /**
   * The IANA zone this view's times are told in: THE READER'S (B-181), or the
   * league's for an account that has none yet. For labels only — every rule
   * behind these times (when a run processes, when a player clears) is the
   * league's, computed on the server, and `processesAt` and `clearsAt` are
   * instants. It was the league's own zone until 2026-09-25.
   */
  timeZone: z.string().min(1),
  system: WaiverSystem,
  /** "Processes Wednesday at 3:00 AM. Bids are sealed until then." */
  headline: z.string(),
  /** The next processing instant; null for continuous and none. */
  processesAt: Iso.nullable(),
  /**
   * My budget, in a budget league. The `reserved` field that summed my pending
   * bids is gone: under PLAN-2026-09-24 §2 rule 5 a pending claim holds back
   * nothing — a bid is checked against all of `left` when it is placed, and the
   * run spends in my claims' order (B-141).
   */
  budget: z.object({ left: z.number(), total: z.number() }).nullable(),
  /** My priority, where it comes from, and why in a sentence, when the system has one. */
  priority: z
    .object({
      rank: z.number().int(),
      of: z.number().int(),
      source: WaiverPrioritySource,
      why: z.string(),
    })
    .nullable(),
  myClaims: z.array(ClaimView),
  /** Soonest to clear first, at most `ON_WAIVERS_LIMIT`. */
  onWaivers: z.array(WaiverPlayerRow).max(ON_WAIVERS_LIMIT),
  /** True when more players are on waivers than `onWaivers` carries. */
  onWaiversTruncated: z.boolean(),
  /** Newest first, at most `WAIVERS_LOG_LIMIT` entries. */
  log: z.array(LogEntry).max(WAIVERS_LOG_LIMIT),
  /** True when older entries were left off `log`. */
  logTruncated: z.boolean(),
  /**
   * The entries older than `log`, a page at a time (B-526): pass it as
   * `?cursor=` to `GET /leagues/:id/transactions?filter=all`, whose pages carry
   * the next. Null when `log` is the whole log (`logTruncated` false).
   */
  logCursor: z.string().nullable(),
  acquisitions: z.object({
    usedThisPeriod: z.number().int(),
    perPeriod: z.number().int().nullable(),
    usedThisSeason: z.number().int(),
    perSeason: z.number().int().nullable(),
  }),
  /**
   * Every team, best place first when the league has an order (the one the
   * next run reads), in seat order when it has none: each team's place and
   * budget left, for every member, as ESPN, Sleeper and Yahoo show them (J7,
   * COMMISSIONER_OVERRIDES.md). Empty before the draft and after the season,
   * when there is no run to read an order.
   */
  order: z.array(WaiverOrderRow).default([]),
  /** Where the order comes from (`priority.source`, for the league); null when it has none. */
  orderSource: WaiverPrioritySource.nullable().default(null),
  /**
   * What the commissioner of a real league can change here now (J7); null for
   * everybody else, and in the practice league, which has no commissioner.
   */
  commissioner: WaiverCommissionerView.nullable().default(null),
  updatedAt: Iso,
});
export type WaiversView = z.infer<typeof WaiversView>;

/**
 * How long a player added from free agency stays off waivers when dropped:
 * dropped within this many hours of the add, the player goes straight back to
 * free agency (PLAN-2026-09-24 §2 rule 4, Sleeper's rule). It closes the move
 * of parking a player on waivers so nobody else can add him; the five-minute
 * undo is for a mistake, and this is for a strategy. Fixed, not a setting.
 */
export const RECENT_ADD_HOURS = 24;

/**
 * Why a dropped player skips waivers:
 *
 * - `no-waivers` — the league sends no drop there (`system: 'none'`, or
 *   `droppedPlayersGoToWaivers` off);
 * - `recent-add` — the team added the player from free agency less than
 *   `RECENT_ADD_HOURS` before the drop.
 */
export const FreeAgencyBecause = z.enum(['no-waivers', 'recent-add']);
export type FreeAgencyBecause = z.infer<typeof FreeAgencyBecause>;

/**
 * Where a dropped player goes: onto waivers until `clearsAt`, or straight to
 * free agency and why. Worked out for the instant the drop happens — now for
 * an add or a drop, the run for a claim — by `dropDestination` in
 * `@fsp/waivers`, the one answer the move itself and every view of it use.
 */
export const DropDestination = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('waivers'), clearsAt: Iso }),
  z.object({ kind: z.literal('free-agency'), because: FreeAgencyBecause }),
]);
export type DropDestination = z.infer<typeof DropDestination>;

/** A roster player as the drop picker shows it, with the consequence of dropping. */
export const DropOption = z.object({
  player: PlayerSummary,
  game: GameStatus.nullable(),
  /**
   * The stored projection for the week a move lands in, in this league's
   * scoring: the pool's number for him (round 3). Null when nobody projected him.
   */
  projection: z.number().nullable(),
  slot: z.string(),
  starting: z.boolean(),
  locked: z.boolean(),
  /**
   * "Your only TE." "On a bye this period." "Locked: the game started." And,
   * when this player would skip waivers as a recent add, that the player goes
   * straight to free agency — said before the move is confirmed, not after.
   */
  consequence: z.string().nullable(),
  /**
   * False for a `locked` player unless the drop happens after the lock lifts:
   * a claim's drop happens at its run, so a claim decided after the period
   * ends can name him and one decided inside it cannot (`dropLockHolds` in
   * `@fsp/waivers`, B-142). An add's drop happens now, while he is locked.
   */
  droppable: z.boolean(),
  /**
   * Where this player goes if dropped in this move, as a fact: per option,
   * because the 24-hour rule is per player. For the instant the drop happens,
   * which is now for an add and the run for a claim. Null when that instant is
   * not known yet: a trade's drops happen when the trade executes.
   */
  dropsTo: DropDestination.nullable(),
});
export type DropOption = z.infer<typeof DropOption>;

/** Everything the add, claim, and drop sheets need for one player. */
export const RosterMoveView = z.object({
  kind: z.enum(['add', 'claim', 'drop']),
  player: PlayerSummary,
  game: GameStatus.nullable(),
  /**
   * The stored projection for the week a move lands in, in this league's
   * scoring: the pool's number for him (round 3). Null when nobody projected him.
   */
  projection: z.number().nullable(),
  processesAt: Iso.nullable(),
  /**
   * FAAB only. `max` is the whole of `left`: a pending claim holds none of the
   * budget back (PLAN-2026-09-24 §2 rule 5, B-141), so a second big bid is as
   * placeable as the first and the run spends in my claims' order.
   */
  bid: z
    .object({ min: z.number(), max: z.number(), allowZero: z.boolean(), left: z.number() })
    .nullable(),
  /** Whether a drop is required (roster full). */
  mustDrop: z.boolean(),
  roster: z.array(DropOption),
  /** My other pending claims, for "only if my claim for X fails". */
  otherClaims: z.array(
    z.object({ claimId: z.string(), playerName: z.string(), order: z.number().int() }),
  ),
  conditionalAllowed: z.boolean(),
  /** Where a new claim lands among mine: last. */
  defaultOrder: z.number().int(),
  /** The pending claim being edited, when there is one. */
  existing: z
    .object({
      claimId: z.string(),
      bid: z.number().nullable(),
      dropPlayerIds: z.array(z.string()),
      order: z.number().int(),
      conditionalOnClaimId: z.string().nullable(),
    })
    .nullable(),
  /** For a drop: what happens to the player, in a sentence. */
  dropConsequence: z.string().nullable(),
  /**
   * For a drop: where the player goes, as a fact — the same answer
   * `dropConsequence` says in words, so a client can state it its own way.
   * Null for an add or a claim, whose possible drops each carry theirs on
   * `roster` (`DropOption.dropsTo`).
   */
  dropsTo: DropDestination.nullable(),
  /** When the move cannot be made at all. */
  blocked: z.string().nullable(),
});
export type RosterMoveView = z.infer<typeof RosterMoveView>;

// ── Trades (TRADES.md) ────────────────────────────────────────────────────

export const TradeStatus = z.enum([
  'proposed',
  'countered',
  'review',
  'accepted',
  'executed',
  'vetoed',
  'declined',
  'withdrawn',
  'expired',
  'void',
]);
export type TradeStatus = z.infer<typeof TradeStatus>;

/** A roster player as the builder and the trade sides show him. */
export const TradeRosterRow = z.object({
  player: PlayerSummary,
  /** This period's projection; null in a real league where none is stored yet. */
  projection: z.number().nullable(),
  /**
   * His worth to a trade (DIRECTION A19, B-185): his points a week over the
   * rest of the league's season, whatever slot he sits in — his form, the
   * house projection before its opponent, moved week by week by his role on
   * the depth chart and the injury report, this week and next at their
   * projections so moved, then over his club's games left: nothing in its bye
   * week, each game moved by its opponent (since 2026-10-23). What the
   * read-out sums and the bots weigh.
   * The practice league weighs him at his season's points a game. Null only
   * from a host that weighs nobody, which neither does. Added 2026-10-22.
   */
  worth: z.number().nullable().default(null),
  /**
   * This season in the league's scoring, the Players row's arithmetic: points
   * a game over the weeks he scored in, and the season's points. Null before
   * his first, and in the practice league's offers. Added 2026-10-17 (a
   * trade weighs a season, not one week).
   */
  seasonAvg: z.number().nullable().default(null),
  seasonPoints: z.number().nullable().default(null),
  game: GameStatus.nullable(),
  locked: z.boolean(),
  slot: z.string(),
  starting: z.boolean(),
});
export type TradeRosterRow = z.infer<typeof TradeRosterRow>;

/**
 * A draft pick as a trade carries it (PLAN-2026-10-27-the-next-season §2.6):
 * a season's draft, a round and the seat it belongs to, never an overall
 * number. Added 2026-10-28.
 */
export const DraftPickRef = z.object({
  /** The pick's id, as a trade names it. */
  id: z.string(),
  season: z.number().int(),
  round: z.number().int(),
  /** The seat whose pick it is, as its team is this season. */
  team: TeamRef,
  /** The team that holds it now; null for a pick whose draft has been held. */
  holder: TeamRef.nullable(),
  /** Its place in its round once its draft's order is set, as Sleeper's 1.08 is 8; null before. */
  place: z.number().int().nullable(),
  /** "Gridiron Gang's 2027 1st-round pick". */
  name: z.string(),
  /**
   * What it counts in a trade's read-out (§2.6 rule 7): the worth now of the
   * player at its place in the pool its draft draws from, in points a week.
   * Null where nothing weighs it.
   */
  worth: z.number().nullable(),
});
export type DraftPickRef = z.infer<typeof DraftPickRef>;

export const TradeSideView = z.object({
  team: TeamRef,
  /** What this team gives. */
  players: z.array(TradeRosterRow),
  drops: z.array(PlayerSummary),
  faab: z.number(),
  /** The draft picks this team gives, by id. */
  picks: z.array(z.string()),
  /** The same picks, each named and placed (§2.6). Added 2026-10-28. */
  draftPicks: z.array(DraftPickRef).default([]),
});
export type TradeSideView = z.infer<typeof TradeSideView>;

/** The three explained lines, from the perspective team's point of view. */
export const TradeReadoutView = z.object({
  perspectiveTeamId: z.string(),
  /** Each side's worth over the best free agent at each player's position, summed: what a bot and the lopsided line weigh. */
  value: z.object({ give: z.number(), get: z.number() }),
  /** Each side's worth, summed: the points a week the value sentence says (B-185). Added 2026-10-22. */
  plain: z.object({ give: z.number(), get: z.number() }).default({ give: 0, get: 0 }),
  lineup: z.object({
    mine: z.object({ before: z.number(), after: z.number() }),
    theirs: z.object({ before: z.number(), after: z.number() }),
  }),
  depth: z.array(z.string()),
  sentences: z.array(z.string()),
});
export type TradeReadoutView = z.infer<typeof TradeReadoutView>;

export const TradeAction = z.enum([
  'accept',
  'counter',
  'decline',
  'withdraw',
  'vote',
  'approve',
  'veto',
]);
export type TradeAction = z.infer<typeof TradeAction>;

export const TradeView = z.object({
  id: z.string(),
  status: TradeStatus,
  /** One sentence for the state: "Offered. Dave sends X to Ken for Y." */
  sentence: z.string(),
  proposer: TeamRef,
  /** Index 0 is the proposer. */
  sides: z.tuple([TradeSideView, TradeSideView]),
  isMine: z.boolean(),
  isProposer: z.boolean(),
  note: z.string(),
  createdAt: Iso,
  respondBy: Iso,
  reviewEndsAt: Iso.nullable(),
  executeAt: Iso.nullable(),
  counterOf: z.string().nullable(),
  counteredBy: z.string().nullable(),
  votes: z.object({
    vetoes: z.number().int(),
    needed: z.number().int(),
    iVoted: z.boolean(),
    canVote: z.boolean(),
  }),
  actions: z.array(TradeAction),
  /**
   * What the trade does to each lineup, for the reader. Null in a league that
   * turned the trade read-out off, or projections, which it is built from
   * (`LeagueRef.tools`, RULES_SCHEMA §15c); nullable since 2026-10-29.
   */
  readout: TradeReadoutView.nullable(),
  /** Set when accepting needs me to pick drops: the problem my roster would have. */
  needsDropsOnAccept: z.string().nullable(),
  /** The offer, its counters, and their counters, oldest first. */
  thread: z.array(
    z.object({
      tradeId: z.string(),
      at: Iso,
      by: z.string(),
      summary: z.string(),
      note: z.string(),
    }),
  ),
  /** "2 days to answer" · "Review ends Wed 9:00 PM" · "Tue 3:00 AM". */
  timeLabel: z.string(),
});
export type TradeView = z.infer<typeof TradeView>;

/** A player marked available (TRADES.md "Trade block"), with what the owner wants back. */
export const TradeBlockEntry = z.object({
  team: TeamRef,
  player: PlayerSummary,
  projection: z.number().nullable(),
  wants: z.string(),
  since: Iso,
  isMine: z.boolean(),
});
export type TradeBlockEntry = z.infer<typeof TradeBlockEntry>;

/**
 * Where each roster is deep and thin, position by position (TRADES.md "find a
 * trade partner", B-388; `rosterDepth` in `@fsp/trades`). A position's TIER
 * is the league's best players there by points a game this season, of those
 * on a roster: the teams times the slots a team starts there alone, and
 * anybody level with the last. A flex is no position's own and is not
 * counted. Deep is more of the tier than a team starts; thin is fewer.
 */
export const RosterDepthView = z.object({
  /** Each position a starting slot is for alone, in the lineup's order. */
  positions: z.array(
    z.object({
      position: z.string(),
      /** The slots a team starts there. */
      slots: z.number().int(),
      /** How many players the tier holds. */
      tier: z.number().int(),
    }),
  ),
  /** Every team in the league's order, with how many of each tier it holds, in `positions` order. */
  teams: z.array(
    z.object({
      team: TeamRef,
      held: z.array(z.object({ position: z.string(), count: z.number().int() })),
    }),
  ),
  /**
   * The teams deep where mine is thin, with the positions that matched:
   * those also thin where mine is deep first, then the most matches. Empty
   * for a reader with no team. A shortlist, never an offer.
   */
  partners: z.array(
    z.object({
      teamId: z.string(),
      /** Where they hold more of the tier than they start and I hold fewer than I start. */
      theyHaveMore: z.array(z.string()),
      /** Where I hold more than I start and they hold fewer than they start; may be empty. */
      youHaveMore: z.array(z.string()),
    }),
  ),
});
export type RosterDepthView = z.infer<typeof RosterDepthView>;

export const TradesView = z.object({
  league: LeagueRef,
  header: z.object({
    deadlinePeriod: z.number().int().nullable(),
    deadlineLabel: z.string(),
    reviewRule: z.string(),
    open: z.boolean(),
    closedReason: z.string().nullable(),
  }),
  forMe: z.array(TradeView),
  sent: z.array(TradeView),
  review: z.array(TradeView),
  history: z.array(TradeView),
  /** Mine first, then the league's, newest first. */
  block: z.array(TradeBlockEntry).default([]),
  /**
   * Each roster's depth by position and the teams that fit mine (B-388).
   * Null before any rostered player has a game this season, while trades
   * are closed, and where a host keeps no season (the practice league's
   * one week). Added 2026-10-17.
   */
  depth: RosterDepthView.nullable().default(null),
  /**
   * Whether the league trades draft picks now (PLAN-2026-10-27-the-next-
   * season §2.6 rule 2), and the picks that have changed hands in the drafts
   * it can still trade, by season and round. Added 2026-10-28.
   */
  picksOpen: z.boolean().default(false),
  picks: z.array(DraftPickRef).default([]),
  myTeamId: z.string(),
  updatedAt: Iso,
});
export type TradesView = z.infer<typeof TradesView>;

export const TradeBuilderView = z.object({
  partner: TeamRef.nullable(),
  partners: z.array(z.object({ team: TeamRef, openOffers: z.number().int() })),
  mine: z.array(TradeRosterRow),
  theirs: z.array(TradeRosterRow),
  give: z.array(z.string()),
  get: z.array(z.string()),
  /**
   * The draft picks each side holds that the league trades now, and those
   * checked (§2.6). Empty where the league trades none. Added 2026-10-28.
   */
  myPicks: z.array(DraftPickRef).default([]),
  theirPicks: z.array(DraftPickRef).default([]),
  givePicks: z.array(z.string()).default([]),
  getPicks: z.array(z.string()).default([]),
  myDrops: z.array(z.string()),
  /** The most players and picks together a side may send. */
  maxPerSide: z.number().int(),
  readout: TradeReadoutView.nullable(),
  mineNeedsDrop: z.boolean(),
  theirsNeedsDrop: z.boolean(),
  myDropOptions: z.array(DropOption),
  /** Why this cannot be sent as it stands, or null. */
  problem: z.string().nullable(),
  counterOf: z.string().nullable(),
  closedReason: z.string().nullable(),
});
export type TradeBuilderView = z.infer<typeof TradeBuilderView>;

// ── Schedule and playoffs (SCHEDULE.md) ───────────────────────────────────

export const SchedulePeriod = z.object({
  period: PeriodRef,
  kind: z.enum(['regular', 'playoff', 'consolation']),
  rivalry: z.boolean(),
  myMatchup: MatchupSummary.nullable(),
  /**
   * My second game this week, in a league that plays two opponents a week
   * (scenario Q6); `myMatchup` is then my first. Null otherwise. Added
   * 2026-11-01.
   */
  mySecond: MatchupSummary.nullable().default(null),
  /**
   * My week in a league played against the field: my score, place and week
   * record, from the scores alone (no projection). Null in a head-to-head
   * week. Added 2026-10-16.
   */
  myField: FieldRowView.nullable().default(null),
  /** Odd team counts: nobody to play this regular-season period. Never true in a playoff week, where the bracket says who rests. */
  myBye: z.boolean(),
  matchups: z.array(MatchupSummary),
  /**
   * How fresh this period's stat lines are, and since when — the
   * scoreboard's `stats` for the same period, read the same way (BACKLOG
   * L2). A matchup under way reads as live only while this says `live`.
   * Added 2026-09-25 (round 3's review, CONTRACT-1).
   */
  stats: StatsFreshness,
});
export type SchedulePeriod = z.infer<typeof SchedulePeriod>;

/** One side of a team's strength of schedule (`scheduleStrength`, B-386). */
export const StrengthPartView = z.object({
  /** Games in this part: finished for `played`, not yet finished for `remaining`. */
  games: z.number().int(),
  /** The opponents' records now, summed over each meeting: a team met twice counts twice. */
  opponents: z.object({
    wins: z.number().int(),
    losses: z.number().int(),
    ties: z.number().int(),
  }),
  /** The opponents' average score a week, over the games each has finished; null before any has. */
  pointsPerGame: z.number().nullable(),
});
export type StrengthPartView = z.infer<typeof StrengthPartView>;

/** A team's strength of schedule over the regular season (SCHEDULE.md, B-386). */
export const ScheduleStrengthView = z.object({
  teamId: z.string(),
  played: StrengthPartView,
  remaining: StrengthPartView,
  /**
   * Where the schedule still to play stands in the league, 1 the hardest:
   * by the opponents' share of wins (a tie half), then their points a week;
   * teams level share a place. Null with nothing left to play, and for
   * everybody before any game has been finished.
   */
  remainingRank: z.number().int().nullable(),
});
export type ScheduleStrengthView = z.infer<typeof ScheduleStrengthView>;

/** One game of the schedule as it stands before the season opens. */
export const UpcomingScheduleGame = z.object({
  homeTeamId: z.string(),
  /** Null for a week against the field: the team plays the whole league. */
  awayTeamId: z.string().nullable(),
  /** Both teams in one division. */
  divisional: z.boolean(),
  /**
   * The week's second game, in a league that plays two opponents a week
   * (scenario Q6); false for its only or first. Added 2026-11-01.
   */
  secondGame: z.boolean().default(false),
});
export type UpcomingScheduleGame = z.infer<typeof UpcomingScheduleGame>;

/** One regular week of it. */
export const UpcomingScheduleWeek = z.object({
  period: z.number().int().min(1),
  /** The calendar's name for the week once the league has one (from the draft on); null before. */
  label: z.string().nullable(),
  /** A rivalry week (`schedule.rivalryPeriods`), whose rivals play each other. */
  rivalry: z.boolean(),
  games: z.array(UpcomingScheduleGame),
  /** The teams with no game that week, in a league with an odd number of them. */
  byes: z.array(z.string()),
});
export type UpcomingScheduleWeek = z.infer<typeof UpcomingScheduleWeek>;

/**
 * The schedule the season would open with, read before it does
 * (PLAN-2026-10-29-the-schedule-before-the-season §3): drawn from the rules,
 * the teams, their divisions and rivals as they are now, and the league's
 * draw, so a change to any of them changes it. Written when the season opens.
 */
export const UpcomingSchedule = z.object({
  /** `leagues.schedule_draw`: 0 is the order every league was drawn in before draws; a commissioner's draw shuffles it. */
  draw: z.number().int().min(0),
  /** The reader is a commissioner, who may draw it again (`POST …/schedule/draw`). */
  canDraw: z.boolean(),
  /** Every team, to look each game's teams up by id; in no order a reader should rely on. */
  teams: z.array(TeamRef),
  weeks: z.array(UpcomingScheduleWeek),
  /** What the rules asked that this schedule cannot do, in sentences (`buildSeasonSchedule`). */
  problems: z.array(z.string()),
});
export type UpcomingSchedule = z.infer<typeof UpcomingSchedule>;

export const ScheduleView = z.object({
  league: LeagueRef,
  myTeamId: z.string(),
  record: z.object({
    wins: z.number().int(),
    losses: z.number().int(),
    ties: z.number().int(),
    /** Null until a period has been finalized (E6). */
    rank: z.number().int().nullable(),
    of: z.number().int(),
  }),
  regularSeasonEnd: z.number().int(),
  playoffTeamCount: z.number().int(),
  periods: z.array(SchedulePeriod),
  /**
   * Every team's strength of schedule over the regular season, in the
   * order the teams first appear in it (`scheduleStrength`): the regular
   * weeks' head-to-head games only, so empty in a league played against the
   * field. Added 2026-10-17 (B-386).
   */
  strength: z.array(ScheduleStrengthView).default([]),
  /**
   * While no regular game is written, the schedule the season would open
   * with; null once it has opened. Added 2026-10-29
   * (PLAN-2026-10-29-the-schedule-before-the-season); absent reads as null.
   */
  upcoming: UpcomingSchedule.nullable().default(null),
  updatedAt: Iso,
});
export type ScheduleView = z.infer<typeof ScheduleView>;

/** One side of a bracket game; `seed` 0 with a `source` means "to be decided". */
export const BracketSlot = z.object({
  seed: z.number().int(),
  team: TeamRef,
  /** "Winner of 3 vs 6" for a slot not yet decided. */
  source: z.string().nullable(),
});
export type BracketSlot = z.infer<typeof BracketSlot>;

export const BracketGame = z.object({
  id: z.string(),
  /** "3 vs 6", "1 rests". */
  label: z.string(),
  top: BracketSlot.nullable(),
  bottom: BracketSlot.nullable(),
  matchupId: z.string().nullable(),
  status: z.enum(['tbd', 'pre', 'live', 'final', 'bye']),
  /** The team that advances (the loser, in a toilet bowl). */
  winnerTeamId: z.string().nullable(),
  topScore: z.number().nullable(),
  bottomScore: z.number().nullable(),
});
export type BracketGame = z.infer<typeof BracketGame>;

export const BracketRound = z.object({
  name: z.string(),
  period: PeriodRef,
  games: z.array(BracketGame),
});
export type BracketRound = z.infer<typeof BracketRound>;

export const BracketView = z.object({
  league: LeagueRef,
  status: z.enum(['projected', 'live', 'final']),
  headline: z.string(),
  teamCount: z.number().int(),
  byes: z.number().int(),
  rounds: z.array(BracketRound),
  consolation: z.array(BracketRound).nullable(),
  consolationKind: z.enum(['none', 'bracket', 'toilet-bowl']),
  champion: TeamRef.nullable(),
  seeds: z.array(
    z.object({
      seed: z.number().int(),
      team: TeamRef,
      status: z.enum(['clinched', 'alive', 'eliminated']),
    }),
  ),
  throughPeriod: z.number().int(),
  updatedAt: Iso,
});
export type BracketView = z.infer<typeof BracketView>;

// ── Awards, milestones, the trophy room (AWARDS.md) ───────────────────────

export const AwardTone = z.enum(['glory', 'shame', 'fun']);
export type AwardTone = z.infer<typeof AwardTone>;
export const AwardTier = z.enum(['bronze', 'silver', 'gold', 'platinum']);
export type AwardTier = z.infer<typeof AwardTier>;
/** Icon ids the kit maps to glyphs; the engine never renders. */
export const AwardIcon = z.string();

export const AwardWinnerView = z.object({
  team: TeamRef,
  value: z.number(),
  /** "131.2", "4.6", "3". Empty for bracket awards. */
  valueLabel: z.string(),
});
export type AwardWinnerView = z.infer<typeof AwardWinnerView>;

export const AwardCardView = z.object({
  /** The mint id once minted, else a computed key. */
  id: z.string(),
  awardId: z.string(),
  name: z.string(),
  blurb: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  scope: z.enum(['period', 'season']),
  period: z.number().int().nullable(),
  /** The leader while the period or season is still open. */
  asItStands: z.boolean(),
  winners: z.array(AwardWinnerView),
  /** The top five, best first. */
  standings: z.array(z.object({ team: TeamRef, valueLabel: z.string() })),
  line: z.string(),
  at: Iso.nullable(),
});
export type AwardCardView = z.infer<typeof AwardCardView>;

/** A milestone's tier reached by a team (ACHIEVEMENTS.md §17: a league thing, not the account's). */
export const MilestoneUnlockView = z.object({
  id: z.string(),
  team: TeamRef,
  milestoneId: z.string(),
  name: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  tier: AwardTier,
  line: z.string(),
  at: Iso,
  /**
   * The week that earned it ("Week 6"); "Before this season" for a career
   * tier the seasons before reached. Null where nothing recorded the week.
   * Added 2026-10-29 (PLAN-2026-10-29-milestones-as-they-happen).
   */
  earnedIn: z.string().nullable().default(null),
});
export type MilestoneUnlockView = z.infer<typeof MilestoneUnlockView>;

/**
 * A milestone a team the person runs has just earned, waiting to be shown to
 * them as a card, once on any device (`MePulse.milestones`,
 * PLAN-2026-10-29-milestones-as-they-happen §2). `id` is what
 * `POST /api/me/milestones/seen` takes; `teamId` opens the team's trophy room.
 */
export const MilestoneMoment = z.object({
  id: z.string(),
  league: z.object({ id: z.string(), name: z.string() }),
  teamId: z.string(),
  milestoneId: z.string(),
  name: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  tier: AwardTier,
  line: z.string(),
  /** As `MilestoneUnlockView.earnedIn`. */
  earnedIn: z.string().nullable(),
});
export type MilestoneMoment = z.infer<typeof MilestoneMoment>;

export const AwardsView = z.object({
  league: LeagueRef,
  period: PeriodRef,
  status: z.enum(['pre', 'live', 'final']),
  /** Periods with awards to show, ascending; the current one is last. */
  periods: z.array(z.number().int()),
  /**
   * This period's league awards.
   *
   * `perPeriod`, not `weekly`: it sat beside `periods` on the same axis in two
   * vocabularies, and it is a served shape a second client programs against
   * (AUDIT-2026-09-21). "Week" is "period" everywhere.
   */
  perPeriod: z.array(AwardCardView),
  season: z.object({
    status: z.enum(['race', 'final']),
    champion: z
      .object({ team: TeamRef, trophyName: z.string(), line: z.string(), season: z.number().int() })
      .nullable(),
    awards: z.array(AwardCardView),
  }),
  recentUnlocks: z.array(MilestoneUnlockView),
  /** My awards this period, for Home. */
  mine: z.array(AwardCardView),
  trophyName: z.string(),
  updatedAt: Iso,
});
export type AwardsView = z.infer<typeof AwardsView>;

export const TrophyView = z.object({
  awardId: z.string(),
  name: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  line: z.string(),
  champion: z.boolean(),
});
export type TrophyView = z.infer<typeof TrophyView>;

export const MedalView = z.object({
  awardId: z.string(),
  name: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  count: z.number().int(),
  careerCount: z.number().int(),
});
export type MedalView = z.infer<typeof MedalView>;

/** A milestone on the trophy room's badge wall, tiered (ACHIEVEMENTS.md §17). */
export const MilestoneView = z.object({
  id: z.string(),
  name: z.string(),
  blurb: z.string(),
  icon: AwardIcon,
  tone: AwardTone,
  scope: z.enum(['season', 'career']),
  value: z.number(),
  valueLabel: z.string(),
  tier: AwardTier.nullable(),
  earnedLine: z.string().nullable(),
  nextTier: AwardTier.nullable(),
  nextThreshold: z.number().nullable(),
  /** "2 of 3 to bronze", "Win 3 in a row". */
  nextLabel: z.string().nullable(),
  progress: z.number().min(0).max(1),
  tiers: z.array(
    z.object({
      tier: AwardTier,
      threshold: z.number(),
      earned: z.boolean(),
      /**
       * When the league recorded it, as `MilestoneUnlockView.earnedIn` says
       * it; null for a tier not earned or not recorded. Added 2026-10-29.
       */
      earnedIn: z.string().nullable().default(null),
    }),
  ),
});
export type MilestoneView = z.infer<typeof MilestoneView>;

export const TrophySeasonView = z.object({
  season: z.number().int(),
  isCurrent: z.boolean(),
  finish: z.string(),
  record: z.string(),
  trophies: z.array(TrophyView),
  medals: z.array(MedalView),
});
export type TrophySeasonView = z.infer<typeof TrophySeasonView>;

export const TrophyRoomView = z.object({
  league: LeagueRef,
  team: TeamRef,
  owner: z.string(),
  isMine: z.boolean(),
  /** "3 seasons · 27–14 · 1 title · 2 playoff trips · best week 171.3". */
  headline: z.string(),
  career: z.object({
    seasons: z.number().int(),
    wins: z.number().int(),
    losses: z.number().int(),
    ties: z.number().int(),
    pointsFor: z.number(),
    titles: z.number().int(),
    playoffTrips: z.number().int(),
    bestWeek: z
      .object({ value: z.number(), season: z.number().int(), period: z.number().int().nullable() })
      .nullable(),
  }),
  seasons: z.array(TrophySeasonView),
  milestones: z.array(MilestoneView),
  /** Everyone's rooms are one tap away. */
  managers: z.array(TeamRef),
  shareText: z.string(),
  trophyName: z.string(),
  updatedAt: Iso,
});
export type TrophyRoomView = z.infer<typeof TrophyRoomView>;

// ── The season wrapped (scenario H9, PLAN-2026-10-18-families-and-newcomers §12.1) ──

/**
 * One card of a season wrapped, in the third person by team name, since the
 * image is made to be shared. `value` is the big figure (a record, a score, a
 * count); `team` another team the card names (the rival, the champion, the
 * best week's opponent); `matchupId` the game a card is about; `awards`, on
 * the awards card alone, each award the team won.
 */
export const WrapCardView = z.object({
  id: z.enum(['finish', 'best-week', 'awards', 'pickup', 'rivalry', 'pickem', 'league']),
  title: z.string(),
  value: z.string().nullable(),
  line: z.string(),
  team: TeamRef.nullable(),
  matchupId: z.string().nullable(),
  awards: z.array(
    z.object({
      awardId: z.string(),
      name: z.string(),
      icon: AwardIcon,
      tone: AwardTone,
      period: z.number().int().nullable(),
      line: z.string(),
    }),
  ),
});
export type WrapCardView = z.infer<typeof WrapCardView>;

/**
 * A team's season told as cards, once its championship hopes are over: the
 * champion decided, the playoff field set without it, or a playoff game lost.
 * Before that `over` is false and there are no cards, an answer rather than a
 * refusal, so a client may ask during the playoffs and learn when to offer it.
 */
export const SeasonWrapView = z.object({
  league: LeagueRef,
  team: TeamRef,
  season: z.number().int(),
  over: z.boolean(),
  /** How it ended; null while it has not. `finished`: a league with no bracket. */
  how: z.enum(['champion', 'runner-up', 'playoffs', 'missed', 'finished']).nullable(),
  cards: z.array(WrapCardView),
  /** The words of the one image to share; null while not over. */
  share: z
    .object({
      kicker: z.string(),
      headline: z.string(),
      subline: z.string(),
      line: z.string(),
      tone: AwardTone,
    })
    .nullable(),
  updatedAt: Iso,
});
export type SeasonWrapView = z.infer<typeof SeasonWrapView>;

// ── The yearbook (scenario H9's league half, PLAN-2026-10-31-the-yearbook) ──

/**
 * The league's season told once its champion is crowned: the champion, the
 * class (every team, the champion and the runner-up first, then the regular
 * season's order), the league records the season set, the games to remember
 * and the moves that paid. In the third person by team name, as the wrap is.
 * Before the champion `over` is false and everything is null or empty, an
 * answer rather than a refusal.
 */
export const YearbookView = z.object({
  league: LeagueRef,
  season: z.number().int(),
  over: z.boolean(),
  champion: z
    .object({
      team: TeamRef,
      runnerUp: TeamRef.nullable(),
      /** "2026 League Champion": the trophy's name, a title worn. */
      title: z.string(),
      line: z.string(),
      /** The final, its last leg; null in a league with no bracket. */
      matchupId: z.string().nullable(),
    })
    .nullable(),
  /** Every team in the league, once. */
  teams: z.array(
    z.object({
      team: TeamRef,
      how: z.enum(['champion', 'runner-up', 'playoffs', 'missed', 'finished']),
      /** Its place in the regular season. */
      rank: z.number().int().nullable(),
      /** "10–4", or its points in a league without results. */
      record: z.string().nullable(),
      /** Every season award it won, the glory first, then the fun, then the shame. */
      honors: z.array(
        z.object({ awardId: z.string(), name: z.string(), icon: AwardIcon, tone: AwardTone }),
      ),
      /** How many weekly awards it won. */
      weekly: z.number().int(),
      /** The one thing it is remembered for: its season award, else its weekly award won most, else its best week. */
      tag: z.string(),
      tone: AwardTone,
      icon: AwardIcon.nullable(),
      line: z.string(),
    }),
  ),
  records: z.object({
    /** No past season kept any of the four: this season's bests are the records. */
    first: z.boolean(),
    line: z.string(),
    /** The record book's season records this season set, by beating every past season's. */
    set: z.array(
      z.object({
        id: z.enum(['high-week', 'blowout', 'streak', 'season-points']),
        name: z.string(),
        value: z.string(),
        team: TeamRef,
        when: z.string(),
        line: z.string(),
      }),
    ),
  }),
  /** Up to four final games, never one twice; none from a consolation game. */
  games: z.array(
    z.object({
      id: z.enum(['closest', 'highest', 'blowout', 'upset']),
      title: z.string(),
      /** The margin or the total; null for the upset. */
      value: z.string().nullable(),
      line: z.string(),
      matchupId: z.string(),
      period: z.number().int(),
      /** Each side, and whether the game went to it: neither in a tie. */
      home: z.object({ team: TeamRef, score: z.number(), won: z.boolean() }),
      away: z.object({ team: TeamRef, score: z.number(), won: z.boolean() }),
    }),
  ),
  moves: z.object({
    /** Players picked up, as free agents or off waivers. */
    pickups: z.number().int(),
    /** Players who changed teams in trades. */
    traded: z.number().int(),
    line: z.string(),
    /** The pickup and the trade whose player scored most for his new team after he arrived. */
    items: z.array(
      z.object({
        id: z.enum(['pickup', 'trade']),
        title: z.string(),
        value: z.string(),
        line: z.string(),
        team: TeamRef,
      }),
    ),
  }),
  /** The words of the class photo; null while not over. */
  share: z
    .object({
      kicker: z.string(),
      headline: z.string(),
      subline: z.string(),
      line: z.string(),
      tone: AwardTone,
    })
    .nullable(),
  updatedAt: Iso,
});
export type YearbookView = z.infer<typeof YearbookView>;

// ── The weekly recap (RECAP.md) ───────────────────────────────────────────

/**
 * A name inside a recap line that goes somewhere (B-220): a player to his
 * card, a manager to their team page. Offsets into the line's `text`, in
 * UTF-16 code units (a JavaScript string's), so a client links exactly the
 * words written. Added 2026-09-27; an older answer has none.
 */
export const RecapName = z.object({
  kind: z.enum(['player', 'team']),
  id: z.string(),
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});
export type RecapName = z.infer<typeof RecapName>;

export const RecapLine = z.object({
  text: z.string(),
  team: TeamRef.nullable(),
  matchupId: z.string().nullable(),
  emphasis: z.boolean(),
  names: z.array(RecapName).default([]),
});
export type RecapLine = z.infer<typeof RecapLine>;

export const RecapSection = z.object({
  id: z.enum(['games', 'stars', 'bench', 'standings', 'wire', 'next']),
  title: z.string(),
  lines: z.array(RecapLine),
});
export type RecapSection = z.infer<typeof RecapSection>;

export const RecapView = z.object({
  league: LeagueRef,
  period: PeriodRef,
  status: z.enum(['pre', 'live', 'final']),
  /** Periods with a recap, ascending. */
  periods: z.array(z.number().int()),
  headline: z.string().nullable(),
  standfirst: z.string().nullable(),
  sections: z.array(RecapSection),
  awards: z.array(AwardCardView),
  /** My week in one sentence, when I have a team in this league. */
  mine: z.string().nullable(),
  at: Iso.nullable(),
  updatedAt: Iso,
});
export type RecapView = z.infer<typeof RecapView>;

/**
 * One side of a shared game (`ChatCard` `matchup`): the team as it looked when
 * shared, as a sticker keeps it, and its score then; null before it has one.
 */
export const SharedSide = z.object({
  teamId: z.string(),
  name: z.string(),
  abbreviation: z.string(),
  colors: z.object({ primary: Hex, secondary: Hex }),
  badge: TeamBadge.default(null),
  score: z.number().nullable(),
});
export type SharedSide = z.infer<typeof SharedSide>;

/**
 * A card body on a post (CHAT.md): on the room's own, the weekly awards, the
 * champion, a team's milestone, a person's achievement, the recap; on a
 * manager's, a mock draft they shared, a sticker, a player or a game.
 */
export const ChatCard = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('awards'),
    /** "Week 3 awards", "2026 season awards". */
    title: z.string(),
    period: z.number().int().nullable(),
    items: z.array(
      z.object({
        awardId: z.string(),
        name: z.string(),
        icon: AwardIcon,
        tone: AwardTone,
        team: TeamRef,
        valueLabel: z.string(),
        line: z.string(),
      }),
    ),
  }),
  z.object({
    kind: z.literal('champion'),
    season: z.number().int(),
    team: TeamRef,
    trophyName: z.string(),
    line: z.string(),
    runnerUp: TeamRef.nullable(),
  }),
  z.object({
    kind: z.literal('milestone'),
    team: TeamRef,
    milestoneId: z.string(),
    name: z.string(),
    icon: AwardIcon,
    tone: AwardTone,
    tier: AwardTier,
    line: z.string(),
  }),
  z.object({
    /**
     * A person's achievement, earned from this league's play (ACHIEVEMENTS.md
     * §12): posted when the league and the person both allow it. The name of a
     * hidden one is revealed by being found; its id never is.
     */
    kind: z.literal('achievement'),
    /** Who found it: their display name. */
    finder: z.string(),
    name: z.string(),
    icon: z.string(),
    tone: AchievementTone,
    /** How it showed before it was found: a secret is said as one. */
    shows: AchievementShows,
    line: z.string(),
  }),
  z.object({
    kind: z.literal('recap'),
    period: z.number().int(),
    headline: z.string(),
    standfirst: z.string(),
    /** The first few game lines. */
    lines: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('draft-recap'),
    headline: z.string(),
    /**
     * Up to three short lines under the headline, each shown as it is: a real
     * league's are the first picks and who made them ("1.01 · Christian
     * McCaffrey, to Grandma Dot", B-339), the grades being the recap's; the
     * practice league's are its top three grades.
     */
    lines: z.array(z.string()),
  }),
  z.object({
    /**
     * A league mock opened for anybody in the league to join (MOCK_DRAFT.md
     * §6.2), posted by the room in the name of who opened it.
     */
    kind: z.literal('mock-room'),
    mockId: z.string(),
    openedBy: z.string(),
    pickSeconds: z.number().int(),
  }),
  z.object({
    /**
     * A mock draft its manager shared (MOCK_DRAFT.md §6.1), posted as their
     * own message. What it says is fixed when it is shared; the mock it opens
     * may since have gone, which the mock's own read says.
     */
    kind: z.literal('mock'),
    mockId: z.string(),
    /** Who ran it: their display name. */
    owner: z.string(),
    slot: z.number().int(),
    teams: z.number().int(),
    grade: z.string(),
    /** Their first three picks, by short name. */
    picks: z.array(z.string()),
  }),
  z.object({
    /**
     * A sticker (PLAN-2026-10-30-stickers): the writer's team's character in
     * a pose, with words on it, sent as their own message. `sticker` is a
     * string rather than the list's enum, and its words ride beside it, so a
     * client older than a new sticker still says what was sent
     * (`stickerOf` finds the pose). Added 2026-10-30.
     */
    kind: z.literal('sticker'),
    sticker: z.string(),
    words: z.string(),
    /**
     * The writer's team as the sticker draws it, AS IT WAS WHEN SENT: a
     * picture sent stays the picture it was, whatever the team wears later or
     * whoever takes the seat. Null for a writer who runs no team in the
     * league, whose own face (`ChatMessage.authorFace`) or initial holds it.
     */
    team: z
      .object({
        abbreviation: z.string(),
        colors: z.object({ primary: Hex, secondary: Hex }),
        badge: TeamBadge.default(null),
      })
      .nullable(),
  }),
  z.object({
    /**
     * A player a manager shared (PLAN-2026-10-31-players-and-games-in-chat
     * §2), as their own line: who he is as he was when shared. A tap opens
     * his card as it is now, where his numbers are current; the card carries
     * none, since a number on a line is read days later as today's.
     *
     * `words` says whether the line's `body` is the writer's own words, drawn
     * over the card; false when it was sent alone, and `body` is the card's
     * own words for a client that does not draw it. Added 2026-10-31.
     */
    kind: z.literal('player'),
    player: z.object({
      id: z.string(),
      name: z.string(),
      shortName: z.string(),
      positions: z.array(z.string()),
      proTeam: z.string().nullable(),
    }),
    words: z.boolean(),
  }),
  z.object({
    /**
     * A game a manager shared (PLAN-2026-10-31-players-and-games-in-chat
     * §2): the week, both teams as they looked and each side's score AS IT
     * WAS WHEN SHARED, with `state` then and `at`. Words are written against
     * that score, and a tap opens the game as it is now. `words` as on a
     * player's. Added 2026-10-31.
     */
    kind: z.literal('matchup'),
    matchupId: z.string(),
    period: z.number().int(),
    /** The calendar's own word for the week ("Week 8"). */
    label: z.string(),
    home: SharedSide,
    away: SharedSide,
    state: z.enum(['pre', 'live', 'final']),
    at: Iso,
    words: z.boolean(),
  }),
  z.object({
    /**
     * The weekly hat, when a week settles (PLAN-2026-10-30-the-weekly-hat):
     * who wears it, who picks, the hat it starts in, and the line that says
     * so. Posted by the room. Added 2026-10-30.
     */
    kind: z.literal('hat'),
    period: z.number().int(),
    hat: WeeklyHatSchema,
    wearers: z.array(TeamRef),
    pickers: z.array(TeamRef),
    line: z.string(),
  }),
]);
export type ChatCard = z.infer<typeof ChatCard>;

// ── League history (HISTORY.md) ───────────────────────────────────────────

export const HistorySeason = z.object({
  season: z.number().int(),
  /**
   * The league that played it, when that is another season's league the
   * reader was in and can open (PLAN-2026-10-27-the-next-season §2.5); null
   * for this league's own season, and for a season the record book holds
   * without its league (a league brought in from a file, the reader not in
   * that season). Added 2026-10-27.
   */
  leagueId: z.string().nullable().default(null),
  final: z.boolean(),
  champion: TeamRef.nullable(),
  runnerUp: TeamRef.nullable(),
  pointsTitle: TeamRef.nullable(),
  lastPlace: TeamRef.nullable(),
});
export type HistorySeason = z.infer<typeof HistorySeason>;

export const AllTimeRow = z.object({
  team: TeamRef,
  rank: z.number().int(),
  seasons: z.number().int(),
  wins: z.number().int(),
  losses: z.number().int(),
  ties: z.number().int(),
  pct: z.number(),
  titles: z.number().int(),
  playoffTrips: z.number().int(),
  pointsFor: z.number(),
});
export type AllTimeRow = z.infer<typeof AllTimeRow>;

export const RecordView = z.object({
  id: z.string(),
  name: z.string(),
  valueLabel: z.string(),
  holder: TeamRef,
  /** "Week 2, 2026", "2025", "all time". */
  when: z.string(),
  line: z.string(),
});
export type RecordView = z.infer<typeof RecordView>;

export const HeadToHeadRow = z.object({
  team: TeamRef,
  vs: z.array(
    z.object({
      opponent: TeamRef,
      wins: z.number().int(),
      losses: z.number().int(),
      ties: z.number().int(),
      /**
       * The season being played's final games between the two, oldest first,
       * from this team's side: the matchup, its week and kind, both scores
       * and the result as the scorer settled it. Empty once the season is
       * history, whose games are kept only as `seasons`' records. Added
       * 2026-10-18 (B-418).
       */
      games: z
        .array(
          z.object({
            matchupId: z.string(),
            period: z.number().int().min(1),
            kind: z.enum(['regular', 'playoff', 'consolation']),
            score: z.number().nullable(),
            against: z.number().nullable(),
            result: z.enum(['W', 'L', 'T']),
          }),
        )
        .default([]),
      /** Each past season's record between the two, newest first; none with no meeting. Added 2026-10-18. */
      seasons: z
        .array(
          z.object({
            season: z.number().int(),
            wins: z.number().int(),
            losses: z.number().int(),
            ties: z.number().int(),
          }),
        )
        .default([]),
    }),
  ),
});
export type HeadToHeadRow = z.infer<typeof HeadToHeadRow>;

export const HistoryView = z.object({
  league: LeagueRef,
  seasons: z.array(HistorySeason),
  allTime: z.array(AllTimeRow),
  records: z.array(RecordView),
  headToHead: z.array(HeadToHeadRow),
  updatedAt: Iso,
});
export type HistoryView = z.infer<typeof HistoryView>;

// ── The draft recap (DRAFT_RECAP.md) ──────────────────────────────────────

export const DraftPickNote = z.object({
  overall: z.number().int(),
  round: z.number().int(),
  team: TeamRef,
  player: PlayerSummary,
  adp: z.number(),
  /** Picks later than the room expected; negative is a reach. */
  delta: z.number(),
  /**
   * An auction's price (AUCTION.md §7). Given, `adp` is the player's value in
   * the league's money and `delta` the dollars he went under it: positive a
   * bargain, negative a splurge. Null for a pick.
   */
  price: z.number().int().nullable().default(null),
  line: z.string(),
});
export type DraftPickNote = z.infer<typeof DraftPickNote>;

export const DraftTeamGrade = z.object({
  team: TeamRef,
  rank: z.number().int(),
  grade: z.string(),
  line: z.string(),
  bestPick: DraftPickNote.nullable(),
  steals: z.number().int(),
  reaches: z.number().int(),
  positions: z.array(z.object({ position: z.string(), count: z.number().int() })),
  /**
   * Every pick this team made, in the order they made them.
   *
   * ON THE GRADE RATHER THAN A SECOND REQUEST, because "what did they actually
   * draft" is the question a grade provokes and the answer should not cost a
   * round trip. A recap is read once, the morning after, usually on a phone;
   * the whole league's picks is a few kilobytes and the alternative is a tap
   * that spins.
   *
   * Added 2026-09-22, when the only thing you could do on the recap screen was
   * tap a team and land somewhere that said there was nothing to show you.
   */
  picks: z.array(
    z.object({
      overall: z.number().int(),
      round: z.number().int(),
      player: PlayerSummary,
      /** Who decided it. Null for a draft that recorded nothing. */
      by: z.enum(['user', 'queue', 'plan', 'rankings', 'ai', 'keeper']).nullable(),
      /** What an auction's winner paid; null for a pick. */
      price: z.number().int().nullable().default(null),
    }),
  ),
  /**
   * What a bot's draft style came to in its picks, a sentence: "Grandma Dot
   * took 4 Vikings, and no Packers." (AI_MANAGERS.md §4.7). Null for a
   * person's team, and for a bot whose tastes did not show.
   */
  style: z.string().nullable().default(null),
});
export type DraftTeamGrade = z.infer<typeof DraftTeamGrade>;

export const DraftRecapView = z.object({
  league: LeagueRef,
  /** False until the league has drafted here. */
  available: z.boolean(),
  completedAt: Iso.nullable(),
  /**
   * What "the room expected" was measured against, in words.
   *
   * REQUIRED, for the reason the house ranking's own `basis` is (D4): a steal
   * is a claim about where a player should have gone, and the list it is
   * measured against is the only way to judge one. There is no average draft
   * position anywhere in this product — nobody has run enough drafts — so it
   * is the league's own board, and saying so is the difference between a
   * number and a number somebody can argue with.
   */
  basis: z.string(),
  headline: z.string().nullable(),
  teams: z.array(DraftTeamGrade),
  steals: z.array(DraftPickNote),
  reaches: z.array(DraftPickNote),
  mine: DraftTeamGrade.nullable(),
  updatedAt: Iso,
});
export type DraftRecapView = z.infer<typeof DraftRecapView>;

// ── The draft replay (PLAN-2026-10-31-the-draft-replay, C11) ──────────────

/**
 * A fact about one pick, worth lingering on in the replay: never advice, the
 * hindsight side of the line. `run`, `first`: the room's, for everybody.
 * `listed`, `passed`, `house`: about the reader's own draft, for them alone.
 * `listed` and `passed` are worked out from their list and told only when
 * every pick it made checks out (§3); `house` is the stored record's.
 */
export const DraftReplayMoment = z.discriminatedUnion('kind', [
  /** A run at one position, said once at its third straight pick; `count` is how many it came to. */
  z.object({
    kind: z.literal('run'),
    position: z.string(),
    count: z.number().int().min(3),
    line: z.string(),
  }),
  /** The first player at a position, after the first round. */
  z.object({ kind: z.literal('first'), position: z.string(), line: z.string() }),
  /**
   * Another team took the name the reader's list had next, the last time
   * before their pick; `missedByOne` when it was the pick just before theirs.
   */
  z.object({ kind: z.literal('listed'), missedByOne: z.boolean(), line: z.string() }),
  /** At the reader's pick, a name higher on their one list that a limit skipped; once a position. */
  z.object({
    kind: z.literal('passed'),
    player: PlayerSummary,
    position: z.string(),
    /** Their own limit, the league's, or the picks left saved for the positions still needed. */
    reason: z.enum(['mine', 'league', 'minimum']),
    line: z.string(),
  }),
  /** The house ranking made the reader's pick, once each time it took over; `ranked` when they had ranked anybody. */
  z.object({ kind: z.literal('house'), ranked: z.boolean(), line: z.string() }),
]);
export type DraftReplayMoment = z.infer<typeof DraftReplayMoment>;

export const DraftReplayPick = z.object({
  overall: z.number().int(),
  /** A pick draft's round; an auction's nth player bought by the team. */
  round: z.number().int(),
  slot: z.number().int(),
  /** The team that made it. */
  teamId: z.string(),
  /** The team whose slot it was in the order, when a trade gave it to `teamId`; null otherwise. */
  originalTeamId: z.string().nullable(),
  player: PlayerSummary,
  by: z.enum(['user', 'queue', 'plan', 'rankings', 'ai', 'keeper']).nullable(),
  /** What an auction's winner paid; null for a pick. */
  price: z.number().int().nullable(),
  moments: z.array(DraftReplayMoment),
});
export type DraftReplayPick = z.infer<typeof DraftReplayPick>;

/**
 * A finished draft, to play back pick by pick (C11). The reader's own: their
 * list's moments are in it for them alone. The pace is a client's, a
 * presentation timeline over this record (PLAN §4), so no time is on a pick.
 */
export const DraftReplayView = z.object({
  league: LeagueRef,
  /** False until the draft is complete; `picks` is empty then. */
  available: z.boolean(),
  type: z.enum(['snake', 'linear', 'third-round-reversal', 'auction']),
  mode: DraftMode,
  rounds: z.number().int(),
  /** When it ran: its start and its end. Null until it has. */
  startedAt: Iso.nullable(),
  completedAt: Iso.nullable(),
  /** Each team and its place in the round-one order: the board's columns. */
  seats: z.array(z.object({ team: TeamRef, slot: z.number().int() })),
  /** Every pick, in the order made. */
  picks: z.array(DraftReplayPick),
  myTeamId: z.string().nullable(),
  updatedAt: Iso,
});
export type DraftReplayView = z.infer<typeof DraftReplayView>;

// ── Home ──────────────────────────────────────────────────────────────────

export const AttentionItem = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('trade-offer'),
    tradeId: z.string(),
    fromTeam: TeamRef,
    /**
     * "Kareem Hunt and a 2027 second for Mahomes."
     *
     * A COPY FIELD, which means it may accompany the fact and never replace
     * it — see `players` below. It was the only carrier of what this trade
     * contained until 2026-09-17, so a client that could not parse English
     * could not tell somebody what they were being offered (audit 3.7).
     */
    summary: z.string(),
    /** Who is on each side, so the sentence is a rendering and not the data. */
    players: z.object({
      give: z.array(PlayerSummary),
      get: z.array(PlayerSummary),
    }),
    expiresAt: Iso,
  }),
  z.object({
    kind: z.literal('waiver-pending'),
    count: z.number().int(),
    processesAt: Iso,
    budgetLeft: z.number().nullable(),
    budget: z.number().nullable(),
  }),
  z.object({ kind: z.literal('vote'), pollId: z.string(), question: z.string(), closesAt: Iso }),
  z.object({ kind: z.literal('draft'), startsAt: Iso, roomOpen: z.boolean() }),
]);
export type AttentionItem = z.infer<typeof AttentionItem>;

export const NewsKind = z.enum(['status', 'practice', 'move', 'league', 'headline']);
export type NewsKind = z.infer<typeof NewsKind>;
export const NewsFilter = z.enum(['mine', 'all', 'injuries', 'moves']);
export type NewsFilter = z.infer<typeof NewsFilter>;

/** One line about a player (PLAYER_NEWS.md): a fact we wrote, a league event, or a linked headline. */
export const NewsView = z.object({
  id: z.string(),
  player: PlayerSummary,
  headline: z.string(),
  source: z.string(),
  publishedAt: Iso,
  kind: NewsKind.default('status'),
  severity: z.enum(['info', 'warn', 'out']).default('info'),
  body: z.string().nullable().default(null),
  /** Outside headlines link to their source; our own lines do not. */
  link: z.string().nullable().default(null),
  /** On my roster. */
  mine: z.boolean().default(false),
  /** Who owns the player in this league, if anyone. */
  team: TeamRef.nullable().default(null),
  /**
   * Who holds him in this league now and what I can do about it, exactly as
   * his Players row says (`PlayerRowView.availability`, `.action`,
   * `.reason`), so a story about a free agent can be acted on where it is
   * read (B-416). Null for a player the league's pool does not have — a
   * position no slot takes, somebody released, retired or on a practice
   * squad — whom nobody here can add. Two reads leave them null for every
   * item, since what they serve says it elsewhere: a player's own news (the
   * card's) and Home's (my roster's). Added 2026-10-18; absent is null.
   */
  inLeague: Availability.nullable().default(null),
  action: PlayerAction.nullable().default(null),
  reason: z.string().nullable().default(null),
});

export const PulseItem = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('chat'),
    id: z.string(),
    author: z.string(),
    authorColor: Hex,
    body: z.string(),
    at: Iso,
    /** A bot said it: left out for a reader who muted the bots (B-299). Absent is a person. */
    bot: z.literal(true).optional(),
  }),
  z.object({ kind: z.literal('transaction'), id: z.string(), summary: z.string(), at: Iso }),
  z.object({
    kind: z.literal('award'),
    id: z.string(),
    title: z.string(),
    team: TeamRef,
    detail: z.string(),
    at: Iso,
  }),
]);

export type PulseItem = z.infer<typeof PulseItem>;
export type NewsView = z.infer<typeof NewsView>;

export const HomeView = z.object({
  league: LeagueRef,
  period: PeriodRef,
  /**
   * My lineup's health, exactly what my lineup read says (`LineupView.health`)
   * for this period. Null means nobody has checked it — a member with no team,
   * or a period with no lineup window — and a `status: 'ok'` from a server
   * that has not looked would be the worst lie on this screen. Made nullable by
   * E1, 2026-09-19; filled from the lineup read since 2026-09-25 (N4).
   */
  health: LineupHealth.nullable(),
  matchup: MatchupSummary.nullable(),
  /**
   * My second game this week, in a league that plays two opponents a week
   * (scenario Q6): my one score against a second team. `matchup` is then my
   * first. Null otherwise. Added 2026-11-01.
   */
  secondMatchup: MatchupSummary.nullable().default(null),
  /**
   * My week in a league played against the field, in place of `matchup`: my
   * score, projection, place and week record. Null in a head-to-head week and
   * for a member with no team. `fieldOf` is how many teams play the week.
   * Added 2026-10-16.
   */
  field: FieldRowView.nullable().default(null),
  fieldOf: z.number().int().nullable().default(null),
  /**
   * How fresh the current period's stat lines are, and since when (BACKLOG L2,
   * N5): \`none\` before any game has kicked off, \`delayed\` while one that has
   * kicked off has no lines, \`final\` once every one that has kicked off has
   * them, \`live\` never until a live feed exists. A 0 on the board beside
   * \`delayed\` is a wait, not a score. Added 2026-09-25.
   */
  stats: StatsFreshness,

  /**
   * What needs me, from the sources that exist: trade offers waiting on me,
   * my pending waiver claims and a scheduled draft. Null means not computed (a
   * member with no team). A vote is not in it yet; the list says nothing about
   * one rather than claiming there is none.
   */
  attention: z.array(AttentionItem).nullable(),
  /**
   * My players' news: the first five of `GET /news?filter=mine` — the injury
   * report as it changes and the league's own moves (PLAN-2026-09-26-every-
   * screen-and-crayon P4). NULL MEANS NOT COMPUTED — a member with no team, or
   * news that could not be read; an empty list is the claim that there is
   * none. Same shape as `HubView.chores`, same reason.
   */
  news: z.array(NewsView).nullable(),
  /** Null means not computed, for the same reason as `news`. */
  pulse: z.array(PulseItem).nullable(),
  /**
   * Me and my neighbors in the table, cut from the whole of it. A store may
   * leave each row's `results` empty and its `allPlay` and `maxPoints` null
   * here: Home shows none of them, and the standings read serves them.
   */
  standingsWindow: z.array(StandingsRow),
  /** "Your week in one sentence" (RECAP.md), from Tuesday until the next kickoff. */
  myWeek: z
    .object({ period: z.number().int(), line: z.string(), matchupId: z.string().nullable() })
    .nullable(),
  updatedAt: Iso,
});
export type HomeView = z.infer<typeof HomeView>;

// ── Hub (SITE_HOME.md) ────────────────────────────────────────────────────

/** One thing a league needs from me. The hub strip and the card chips render the same rows. */
export const Chore = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('lineup'),
    leagueId: z.string(),
    leagueName: z.string(),
    headline: z.string(),
    problemCount: z.number().int(),
    /** Which slot to open, and how many bench players could fill it. Never who. */
    swap: z
      .object({
        candidates: z.number().int().positive(),
        rowSlot: z.string(),
        rowSlotIndex: z.number().int(),
      })
      .nullable(),
    locksAt: Iso.nullable(),
  }),
  z.object({
    kind: z.literal('trade-offer'),
    leagueId: z.string(),
    leagueName: z.string(),
    tradeId: z.string(),
    fromOwner: z.string(),
    /** Copy. It accompanies `players`; it never stands in for it. */
    summary: z.string(),
    players: z.object({
      give: z.array(PlayerSummary),
      get: z.array(PlayerSummary),
    }),
    expiresAt: Iso,
  }),
  z.object({
    kind: z.literal('waiver-pending'),
    leagueId: z.string(),
    leagueName: z.string(),
    count: z.number().int(),
    processesAt: Iso,
  }),
  z.object({
    kind: z.literal('draft'),
    leagueId: z.string(),
    leagueName: z.string(),
    startsAt: Iso,
    roomOpen: z.boolean(),
    joined: z.number().int(),
    of: z.number().int(),
  }),
  /**
   * My last settled week's result, from its settling until the next week's
   * first kickoff, while I have not looked at the league's front page since
   * it settled (SITE_HOME "a final result not yet seen").
   */
  z.object({
    kind: z.literal('result'),
    leagueId: z.string(),
    leagueName: z.string(),
    matchupId: z.string(),
    /** The winner the scoring recorded, a tiebreak's included. */
    won: z.boolean(),
    /** A level score with no winner recorded: neither won nor lost. Added 2026-10-19. */
    tied: z.boolean().default(false),
    margin: z.number(),
    opponentName: z.string(),
    /**
     * The week's second game, in a league that plays two opponents a week
     * (scenario Q6), said as the first is; null otherwise. Added 2026-11-01.
     */
    second: z
      .object({
        matchupId: z.string(),
        won: z.boolean(),
        tied: z.boolean(),
        margin: z.number(),
        opponentName: z.string(),
      })
      .nullable()
      .default(null),
  }),
]);
export type Chore = z.infer<typeof Chore>;

/**
 * The order chores are listed in, most urgent first: a draft about to happen,
 * a lineup that cannot score, an offer waiting, the week's result, claims
 * pending. One rule for every server that lists them (the hub, the simulator).
 */
export const CHORE_ORDER: Readonly<Record<Chore['kind'], number>> = {
  draft: 0,
  lineup: 1,
  'trade-offer': 2,
  result: 3,
  'waiver-pending': 4,
};

/**
 * One league, as a screen inside it orients itself.
 *
 * API.md named this and `views.ts` never had it, so E1 is where it gets
 * written down. It is deliberately the SMALL shape: what every league screen
 * needs before it knows which screen it is — who I am here, what period it is,
 * who else is in it — and nothing that belongs to one screen in particular.
 * The standings, the schedule and the scoreboard are their own reads, because
 * a screen that needs one of them should not pay for the other two.
 */
export const LeagueView = z.object({
  league: LeagueRef,
  /** Null for a league in setup, or one whose calendar has not been built. */
  period: PeriodRef.nullable(),
  /** Null for a commissioner with no seat, or somebody watching. */
  myTeam: TeamRef.nullable(),
  teams: z.array(TeamRef),
  /** Which stored version of the rules this league is being played under. */
  settingsVersion: z.number().int(),
  regularSeasonEnd: z.number().int(),
  playoffTeamCount: z.number().int(),
  /** Null until a season is opened — a league with no schedule has no last period. */
  lastScheduledPeriod: z.number().int().nullable(),
});
export type LeagueView = z.infer<typeof LeagueView>;

export const LeagueCard = z.object({
  league: LeagueRef,
  live: z.boolean(),
  myTeam: TeamRef.nullable(),
  period: PeriodRef.nullable(),
  matchup: MatchupSummary.nullable(),
  /**
   * My second game this week, in a league that plays two opponents a week
   * (scenario Q6), as `matchup` is read; null otherwise. Added 2026-11-01.
   */
  secondMatchup: MatchupSummary.nullable().default(null),
  /**
   * My week in a league played against the field, in place of `matchup`,
   * from the scores the last pass wrote and with no projection, as the card's
   * matchup is; `fieldOf` is how many teams play it. Null otherwise. Added
   * 2026-10-16.
   */
  field: FieldRowView.nullable().default(null),
  fieldOf: z.number().int().nullable().default(null),
  /** The week my team went out of an elimination league (Q1); null while alive, and in any other format. */
  eliminatedPeriod: z.number().int().nullable().default(null),
  /**
   * How fresh the current period's numbers are — the scoreboard's `stats` —
   * so the card's matchup reads "In progress" rather than "Live" when they
   * are not live (B-171). Null where `matchup` is: nobody worked it out.
   * Added 2026-09-25 (round 3's review, CONTRACT-1).
   */
  stats: StatsFreshness.nullable(),
  standing: z.object({ rank: z.number().int(), of: z.number().int() }).nullable(),
  members: z.object({ joined: z.number().int(), of: z.number().int() }),
  /** Null means nobody worked out what this league needs. See `HubView.chores`. */
  chores: z.array(Chore).nullable(),
});
export type LeagueCard = z.infer<typeof LeagueCard>;

export const HubView = z.object({
  user: z.object({ displayName: z.string() }),
  leagues: z.array(LeagueCard),
  /**
   * Every league's chores merged, most urgent first — or **null**, meaning
   * nobody worked them out.
   *
   * THE TWO ARE NOT THE SAME THING, and the difference is a sentence on the
   * screen: an empty list renders as "All caught up. Enjoy the week.", which
   * is the wrong thing to say to somebody with an unanswered trade offer. The
   * league store does not compute chores yet and answered `[]`, so the web app
   * carried a rule of its own — `choresComplete: leagues.length === 0` — that
   * lives nowhere in this contract and that a second client would have no way
   * to know about (audit 3.7). Null says it in the payload instead.
   */
  chores: z.array(Chore).nullable(),
  updatedAt: Iso,
});
export type HubView = z.infer<typeof HubView>;

// ── A league before its draft (LEAGUE_SETUP.md, scenarios B1 and B2) ──────

/**
 * A face as others see it (FACES.md §3.4, B-525): a person's own, which they
 * made for their account (`AvatarRef`'s `face`), or a bot's character, on the
 * two colors of its disc. A client draws a person with none by their initial.
 * Added 2026-10-26.
 */
export const PersonFace = z.object({
  face: Face,
  colors: z.object({ primary: Hex, secondary: Hex }),
});
export type PersonFace = z.infer<typeof PersonFace>;

/**
 * One seat in a league that has not drafted.
 *
 * `ownerName` is null for an open seat, which is the whole distinction the
 * screen renders: a filled seat is a person with a team, an empty one is a
 * dashed circle saying "Invite someone". A seat nobody has taken has a
 * placeholder name in the database, and the screen must not show it as
 * though somebody chose it.
 */
export const SeatRef = z.object({
  teamId: z.string(),
  name: z.string(),
  abbreviation: z.string(),
  colors: z.object({ primary: Hex, secondary: Hex }),
  /** Display name, or null for an open seat. Never an id, never an address. */
  ownerName: z.string().nullable(),
  /** The reader runs this team: its manager, or a co-manager of it. */
  isMine: z.boolean(),
  /**
   * The team's co-managers by name, oldest first (B13): they run it beside
   * `ownerName`, its manager. Added 2026-10-18; absent is none.
   */
  coManagers: z.array(z.string()).default([]),
  /** The reader co-manages this team rather than managing it. Added 2026-10-18. */
  isCoManager: z.boolean().default(false),
  /**
   * The manager's own face (`PersonFace`, B-525), beside their name on the
   * members list; null for an open seat and for a manager with none. Added
   * 2026-10-26.
   */
  ownerFace: PersonFace.nullable().default(null),
  /** The team's badge, as `TeamRef.badge` (DIRECTION U25). Added 2026-10-18. */
  badge: TeamBadge.default(null),
  role: z.enum(['commissioner', 'co-commissioner', 'manager', 'spectator']).nullable(),
  /**
   * WHO IS IN THE SEAT, which `ownerName: null` alone cannot say.
   *
   * Four states, and the database has modeled them since `0001` — an owner id
   * and a `team_ai_managers` row are independent:
   *
   *   `manager`   somebody is in it
   *   `bot`       nobody is, and a bot is playing — a friend arriving late
   *               takes this seat over
   *   `open`      nobody has ever been in it: the dashed circle
   *   `vacated`   somebody WAS in it and left, or deleted their account. The
   *               team stands with its name, colors and record, and the screen
   *               names its owner **"Former manager"** rather than pretending
   *               the seat was never filled (ACCOUNTS.md rule 6)
   *
   * Added 2026-09-17 with migration `0008`. A client that does not know the
   * word `vacated` reads it as an open seat, which is the additive-versioning
   * rule working as intended.
   */
  seat: z.enum(['manager', 'bot', 'open', 'vacated']),
  /**
   * An open seat whose team already has its own name, colors and badge — a
   * league brought in from a file (H10, PLAN-2026-10-18 §13.1) — drawn by
   * them, waiting for its manager's link. False for every other seat, whose
   * open name is a placeholder nobody chose. Added 2026-10-19.
   */
  named: z.boolean().default(false),
  /**
   * The bot's manager name while a bot is making this team's moves, which can
   * be true of a seat somebody still owns (they are away) as well as of one
   * nobody does. Null when a person is playing their own team.
   */
  botName: z.string().nullable(),
  /**
   * The band the bot plays at (AI_MANAGERS.md §4.2), while one plays this
   * team; null otherwise. Added 2026-10-19 (B-459).
   */
  botBand: SkillBandId.nullable().default(null),
  /**
   * The link that hands this team to a new manager, while one is out
   * (MANAGERS.md, J4): its code — the link is `/join/<code>`, like the
   * league's — and when it was made. Only ever set for the commissioner and a
   * co-commissioner, who made it and may send it again; null for everybody
   * else, and for a team with no link out. Added 2026-10-01.
   */
  invite: z.object({ code: z.string(), createdAt: Iso }).nullable().default(null),
});
export type SeatRef = z.infer<typeof SeatRef>;

/**
 * Electing a commissioner, when a league has none.
 *
 * WHY A LEAGUE CAN HAVE NONE. The commissioner can leave, be removed, or
 * delete their account — and an account deletion cannot be refused
 * (ACCOUNTS.md rule 6). A co-commissioner succeeds automatically; when there
 * is not one, the seated managers vote: a league whose commissioner leaves
 * elects a new one.
 *
 * NOBODY IS TOLD WHO VOTED FOR WHOM. Each candidate carries a count, and
 * `myVote` is the caller's own vote, which is theirs to see. There is no
 * field, anywhere, that maps a voter to a choice.
 *
 * CANDIDATES ARE SEATS, NOT ACCOUNTS. A candidate is named by `teamId` for the
 * same reason every other league view avoids user ids (§7 invariant 6): a
 * league member can address a seat, and never needs the id behind it.
 */
export const ElectionCandidate = z.object({
  teamId: z.string(),
  displayName: z.string(),
  votes: z.number().int(),
  isMe: z.boolean(),
});
export type ElectionCandidate = z.infer<typeof ElectionCandidate>;

export const ElectionView = z.object({
  leagueId: z.string(),
  /** True while it is still taking votes. False once it has been decided. */
  open: z.boolean(),
  /** 72 hours from the moment it opened. */
  closesAt: Iso,
  candidates: z.array(ElectionCandidate),
  /** How many of the seated managers have voted, and how many there are. */
  votesCast: z.number().int(),
  seatedManagers: z.number().int(),
  /** More than half of `seatedManagers` decides it before the deadline. */
  majorityAt: z.number().int(),
  /** The seat the caller voted for, or null. Never anybody else's. */
  myVote: z.string().nullable(),
  /** True when the caller is a seated manager and may vote. */
  canVote: z.boolean(),
  /** Set once it is decided, with how — for the system message in chat. */
  decided: z
    .object({
      teamId: z.string(),
      displayName: z.string(),
      by: z.enum(['majority', 'most-votes', 'tenure']),
      at: Iso,
    })
    .nullable(),
  updatedAt: Iso,
});
export type ElectionView = z.infer<typeof ElectionView>;

/**
 * What a member sees in a league that has not drafted yet.
 *
 * MEMBERS ONLY, and `inviteCode` is why. `InvitePreview` is the public shape
 * and carries no code — knowing a league exists must not hand out a seat in
 * it. This one is behind the same `league_members` gate as the hub, and a
 * non-member gets the same `not_found` as a league that does not exist.
 */
/** A draft row's status, as `drafts.status` holds it. */
export const LeagueDraftStatus = z.enum(['scheduled', 'live', 'paused', 'complete']);
export type LeagueDraftStatus = z.infer<typeof LeagueDraftStatus>;

export const LeagueSetupView = z.object({
  league: LeagueRef,
  /** The code itself, for saying out loud, and the link is built from it. */
  inviteCode: z.string(),
  seats: z.array(SeatRef),
  /** Null while the commissioner has not picked a time. */
  draftAt: Iso.nullable(),
  /**
   * `snake`, `auction`, … Read from the draft row, not from the rules JSON.
   *
   * The domain enum since 2026-09-17, not `z.string()`: a client generating
   * models from the contract got "some string" for a field with exactly five
   * values, and a value outside them would have reached a screen as text
   * (audit 3.7).
   */
  draftType: DraftType,
  /**
   * Who is present and who enters the picks: `autopick`, `manual` or `live`
   * (settings version 3, BACKLOG D1). Read from the league's rules, which say
   * how it drafts, and null when they cannot be read, like `regularSeason`.
   */
  draftMode: DraftMode.nullable(),
  /** Seconds on the pick clock. Same row, same reason. */
  pickSeconds: z.number().int(),
  /**
   * Where the draft is: `scheduled` until it starts, `live` or `paused` while
   * a live draft is in its room (D10), `complete` once every pick is in. A
   * client reads it to send a manager into a room that has started rather
   * than to wait for one. The draft row's own status, like `draftType`.
   */
  draftStatus: LeagueDraftStatus.default('scheduled'),
  /**
   * A live draft's pick clock at zero: the room picks (from the queue, then
   * priorities, then the board, or without the queue) or waits (`off`), so a
   * screen can say which before draft day. From the league's rules, like
   * `draftMode`, and null when they cannot be read.
   */
  whenTheClockExpires: z.enum(['queue-then-rankings', 'rankings', 'off']).nullable().default(null),
  /**
   * The league's regular season, by period index — `schedule.regularSeason`
   * from its rules. A league made in week 3 plays 3 to the end
   * (PLAN-2026-09-24 item A), and a league in setup has no calendar yet to
   * say so, so this is where it is read. The sport's calendar
   * (`GET /api/sports/:sport/calendar`) turns an index into "Week 3". Null
   * when the league's rules cannot be read.
   */
  regularSeason: z.object({ first: z.number().int(), last: z.number().int() }).nullable(),
  /** True for a commissioner or co-commissioner: the seat-management actions. */
  canManage: z.boolean(),
  /**
   * No member holds `commissioner`.
   *
   * DERIVED, never stored. The alternative was a column on `leagues`, and a
   * column can disagree with the membership rows it describes — this cannot.
   * While it is true, commissioner-only actions are refused and the league's
   * seated managers can elect somebody (§7 invariant 10).
   */
  needsCommissioner: z.boolean(),
  /** The election in progress, or the last one decided. Null when neither. */
  election: ElectionView.nullable(),
  /**
   * The invites by email nobody has joined from yet, newest first
   * (PLAN-2026-10-28-by-email §3.1): sent to a commissioner alone, since each
   * names an address, and absent for everybody else. In this read so a page
   * that reads the league again sees an invite accepted as its seat fills.
   */
  invites: z.array(EmailInvite).optional(),
  updatedAt: Iso,
});
export type LeagueSetupView = z.infer<typeof LeagueSetupView>;

/**
 * One team's lineup this week at a glance, for the commissioner's Managers
 * (MANAGERS.md "A quiet manager", scenario J3).
 *
 * The health is exactly what that team's lineup read says (`LineupView.health`)
 * for the same period; any member may read any lineup, so nothing here is
 * news to a member. The nudge is the commissioner's business, and is null and
 * false for everybody else.
 */
export const LineupGlance = z.object({
  teamId: z.string(),
  health: LineupHealth,
  /**
   * When this team's manager was nudged about this period's lineup, or null.
   * Only ever set for a commissioner or co-commissioner reading.
   */
  nudgedAt: Iso.nullable(),
  /**
   * Whether the reader may nudge this team's manager now: they are a
   * commissioner or co-commissioner, a person other than them runs the team,
   * a slot in its lineup cannot score, and nobody has nudged them this period.
   */
  canNudge: z.boolean(),
});
export type LineupGlance = z.infer<typeof LineupGlance>;

/**
 * Every team's lineup this week at a glance (`GET /api/leagues/:id/lineups`).
 * `period` is null, with no teams, for a league with no week to show: before
 * the draft, or with no calendar.
 */
export const LeagueLineupsView = z.object({
  leagueId: z.string(),
  period: PeriodRef.nullable(),
  teams: z.array(LineupGlance),
  updatedAt: Iso,
});
export type LeagueLineupsView = z.infer<typeof LeagueLineupsView>;

// ── Draft room (DRAFT_ROOM.md, REALTIME.md) ───────────────────────────────

export const DraftSeat = z.object({
  team: TeamRef,
  /** 1-based first-round slot. */
  slot: z.number().int(),
  /** Set when an AI manager holds the seat. */
  ai: z
    .object({
      name: z.string(),
      voice: z.string(),
      difficulty: z.string(),
      /**
       * How it drafts, in a few short phrases, its plan first, then its
       * tastes, then how it reacts to the room ("Starters before backups",
       * "Loves the Vikings", "No Packers"; AI_MANAGERS.md §4.7). Words, never
       * numbers. Filled by the draft room; empty elsewhere, and for a bot with
       * no style for the league's sport.
       */
      style: z.array(z.string()).default([]),
    })
    .nullable(),
  /** Somebody from this seat has the room open now. Only the live room can know; false elsewhere. */
  connected: z.boolean(),
  /**
   * The seat is drafting by itself until its manager turns this off: they
   * chose to, or their clock ran out (PLAN-2026-10-14 §2). False for a bot
   * seat, which picks in its own style, and outside a live room.
   */
  autopilot: z.boolean().default(false),
});

/** One season's production, as a draft board reads it: under the league's own scoring. */
export const DraftSeasonLine = z.object({
  season: z.number().int(),
  games: z.number().int(),
  points: z.number(),
  perGame: z.number(),
});
export type DraftSeasonLine = z.infer<typeof DraftSeasonLine>;

export const DraftBoardCell = z.object({
  overall: z.number().int(),
  round: z.number().int(),
  slot: z.number().int(),
  teamId: z.string(),
  player: PlayerSummary.nullable(),
  /** Who decided it. 'plan' is a manager's own pre-draft list (migration `0014`). */
  by: z.enum(['user', 'queue', 'plan', 'rankings', 'ai', 'keeper']).nullable(),
  isCurrent: z.boolean(),
  /**
   * The team whose slot this is in the order, when a trade gave the pick to
   * `teamId`; null for a pick its own team makes (DRAFT_ROOM.md "Trading
   * during the draft"). A board's column is this team's, or `teamId`'s.
   */
  originalTeamId: z.string().nullable().default(null),
  /** The team that has the drafted player now, when a trade moved him from `teamId`; null otherwise. */
  heldBy: z.string().nullable().default(null),
  /**
   * What an auction's winner paid (AUCTION.md); null for a pick. In an
   * auction `round` is the team's nth player and the square is its column's,
   * in the order bought.
   */
  price: z.number().int().nullable().default(null),
});

/**
 * One thing in a trade during the draft (DRAFT_ROOM.md "Trading during the
 * draft"): a pick not yet made, or a player drafted in this draft. Each
 * carries what a screen needs to name it.
 */
export const DraftTradeItem = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('pick'),
    round: z.number().int(),
    /** The team whose slot it is in the order, which a trade never changes. */
    teamId: z.string(),
    /** Its number in the order as it stands, and its place in its round. */
    overall: z.number().int(),
    slot: z.number().int(),
  }),
  z.object({
    kind: z.literal('player'),
    player: PlayerSummary,
    /** The pick he was drafted with. */
    overall: z.number().int(),
  }),
]);
export type DraftTradeItem = z.infer<typeof DraftTradeItem>;

/** An offer waiting on an answer: `give` goes from `fromTeamId` to `toTeamId`, `get` comes back. */
export const DraftTradeOffer = z.object({
  id: z.string(),
  fromTeamId: z.string(),
  toTeamId: z.string(),
  give: z.array(DraftTradeItem),
  get: z.array(DraftTradeItem),
  at: Iso,
});
export type DraftTradeOffer = z.infer<typeof DraftTradeOffer>;

/** A trade accepted during the draft. */
export const DraftTradeMade = DraftTradeOffer.extend({
  /** 1, 2, … in the order accepted, never reused. */
  seq: z.number().int(),
  acceptedAt: Iso,
});
export type DraftTradeMade = z.infer<typeof DraftTradeMade>;

/** The trading half of a draft room, for one reader. */
export const DraftTrading = z.object({
  /**
   * The league trades during its draft, and this is not a mock. Offers are
   * taken while the draft is not over (`status`), from a reader with a seat to
   * any seat a person holds. Unchanging for a draft, so no event carries it.
   */
  allowed: z.boolean(),
  /** Offers to the reader and from the reader, oldest first; nobody else's. */
  offers: z.array(DraftTradeOffer),
  /** Every trade this draft has made, oldest first. */
  trades: z.array(DraftTradeMade),
});
export type DraftTrading = z.infer<typeof DraftTrading>;

export const DraftPlayerRow = z.object({
  player: PlayerSummary,
  rank: z.number().int(),
  /**
   * Points this player is expected to score, when somebody has expected any.
   *
   * NULLABLE since D6. The house projections are made a week at a time
   * (B-161), not for a season; a board served for a draft that has not been
   * played has a ranking and no forecast, and a zero here is
   * indistinguishable from one somebody computed (ICD-S01: what is not
   * computed is null).
   */
  projection: z.number().nullable(),
  /**
   * Average draft position, when somebody knows one. NULL TODAY AND HONESTLY
   * SO: no source we may use publishes one, and too few drafts run here for
   * our own to mean anything. The REST read filled it with the rank until
   * 2026-10-14, which a screen then labeled ADP.
   */
  adp: z.number().nullable(),
  tier: z.number().int(),
  /** His place among players at his own position on this board: 4 for RB4. Null off the board's order. */
  positionRank: z.number().int().nullable().default(null),
  /** The week his pro team does not play this season, when the schedule says. */
  bye: z.number().int().nullable().default(null),
  /** What he scored the season the board is built from, under this league's scoring. Null for nobody played. */
  last: DraftSeasonLine.nullable().default(null),
  queued: z.boolean(),
  /** Fills a starter slot this team still needs. */
  fillsNeed: z.boolean(),
  /**
   * In an auction, what the house thinks he is worth in the league's money
   * (AUCTION.md §7): a number, not advice. Null in a pick draft.
   */
  value: z.number().int().nullable().default(null),
});

/** A player up for bids in an auction (AUCTION.md §10), as one reader sees him. */
export const DraftAuctionLot = z.object({
  id: z.number().int(),
  player: PlayerSummary,
  nominatedBy: z.string(),
  /** The high bid, and the team that has it. */
  price: z.number().int(),
  leaderTeamId: z.string(),
  /** When he sells; null while the draft is paused. */
  deadlineAt: Iso.nullable(),
  /** The reader's own auto-bid on him, private to them; null for none. */
  myAuto: z.number().int().nullable(),
  /** The most the reader can bid on him now; 0 with no room or no seat. */
  myMost: z.number().int(),
  /** His value (`DraftPlayerRow.value`). */
  value: z.number().int().nullable(),
  /** The bids so far, the last ten, oldest first. Who bid by auto-bid is not said. */
  bids: z.array(z.object({ teamId: z.string(), amount: z.number().int(), at: Iso })),
});
export type DraftAuctionLot = z.infer<typeof DraftAuctionLot>;

/** One team's money in an auction, which every reader sees (AUCTION.md §3.2). */
export const DraftAuctionBudget = z.object({
  teamId: z.string(),
  /** What it has paid. */
  spent: z.number().int(),
  /** What it has left, the players it is ahead on held back. */
  left: z.number().int(),
  /** The most it can bid on a player put up now: `left` less the lowest bid for every other spot. */
  most: z.number().int(),
  /** Spots it has left, the players it is ahead on counted as filled. */
  spotsLeft: z.number().int(),
});
export type DraftAuctionBudget = z.infer<typeof DraftAuctionBudget>;

/** An auction's half of the draft room (AUCTION.md §10). */
export const DraftAuction = z.object({
  rules: z.object({
    budget: z.number().int(),
    minBid: z.number().int(),
    nominationSeconds: z.number().int(),
    bidSeconds: z.number().int(),
    resetSeconds: z.number().int(),
    lotsAtOnce: z.number().int(),
  }),
  /** The players up, the soonest to sell first. */
  lots: z.array(DraftAuctionLot),
  /** Whose nomination it is, and until when; null when nobody is nominating. */
  nominating: z
    .object({ teamId: z.string(), isMine: z.boolean(), deadlineAt: Iso.nullable() })
    .nullable(),
  /** Every team's money, in seat order. */
  budgets: z.array(DraftAuctionBudget),
});
export type DraftAuction = z.infer<typeof DraftAuction>;

/** One league channel for everything (CHAT.md): talk, AI talk, and the room's system posts. */
export const ChatMessage = z.object({
  id: z.string(),
  kind: z.enum(['user', 'system', 'ai']),
  author: z.string(),
  authorColor: Hex.nullable(),
  /**
   * The writer's face beside the line (B-525): a person's own, or a bot's
   * character; null for a person with none, and for the room's own lines.
   * Added 2026-10-26.
   */
  authorFace: PersonFace.nullable().default(null),
  body: z.string(),
  at: Iso,
  /** emoji → owner names who reacted. */
  reactions: z.record(z.string(), z.array(z.string())).default({}),
  /**
   * The reactions that are the READER's own, which `reactions` cannot say: it
   * names people by display name, and two managers can share one. Absent on a
   * message nobody in particular is reading (the draft room's copy).
   */
  myReactions: z.array(z.string()).optional(),
  /** A card body: on a system post the weekly awards, the champion, an unlock; on a manager's, a shared mock. */
  card: ChatCard.optional(),
  /**
   * Hidden by the commissioner (scenario I9, B-449): `body` is then the
   * sentence that says so, for everyone, and its words are nobody's to read
   * here. Added 2026-10-19; absent is shown.
   */
  hidden: z.boolean().optional(),
  /**
   * The line this message answers (PLAN-2026-10-27-more-fun-in-chat C2), for
   * the quote over it: its writer and its words as this reader reads them —
   * masked as they choose (B-448), the hidden sentence once it is hidden — or
   * `gone` once it was deleted. `author` is null for the room's own line, as
   * the feed names no writer for it, and for a line gone. Added 2026-10-27;
   * absent answers nothing.
   */
  replyTo: z
    .object({
      id: z.string(),
      author: z.string().nullable(),
      body: z.string(),
      hidden: z.boolean().optional(),
      gone: z.boolean().optional(),
    })
    .optional(),
  /**
   * The members this message names with `@` (C2), by their name now, and
   * whether one is the reader, for a client to mark the names and a message
   * that calls on me. Added 2026-10-27; absent mentions nobody.
   */
  mentions: z.array(z.object({ name: z.string(), me: z.boolean() })).optional(),
  /**
   * A poll its writer asked the league (PLAN-2026-10-30-polls-in-chat): the
   * question, each option with its votes and who cast them (display names, as
   * a reaction's are) and whether one is the reader's, the votes in all, when
   * it closes and whether it has. Masked as the reader chooses (B-448);
   * absent on a hidden one. Added 2026-10-30.
   */
  poll: z
    .object({
      question: z.string(),
      options: z.array(
        z.object({
          text: z.string(),
          votes: z.number().int(),
          voters: z.array(z.string()),
          mine: z.boolean(),
        }),
      ),
      total: z.number().int(),
      closesAt: Iso,
      closed: z.boolean(),
    })
    .optional(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;
/** The draft room reads the same channel. */
export const DraftChatMessage = ChatMessage;

export const DraftView = z.object({
  league: LeagueRef,
  status: z.enum(['lobby', 'live', 'paused', 'complete']),
  /** The pick order, or `auction`, which has none: its half is `auction`. */
  type: z.enum(['snake', 'linear', 'third-round-reversal', 'auction']),
  /**
   * How the league drafts (settings version 3): by itself at its time
   * (`autopick`), held in person and entered by the commissioner (`manual`,
   * which is `live` while it is being entered, with `current` the next pick
   * and no deadline), or in the room (`live`).
   */
  mode: DraftMode,
  rounds: z.number().int(),
  pickSeconds: z.number().int(),
  /**
   * What a pick clock does at zero, so a screen can say so before it happens:
   * the queue, then priorities, then the board; the same without the queue;
   * or nothing — the soft timer, where the room waits (`ClockExpiry`).
   */
  whenTheClockExpires: z
    .enum(['queue-then-rankings', 'rankings', 'off'])
    .default('queue-then-rankings'),
  /**
   * What `rank` is, in words: the season and scoring it was built from. A
   * ranking with no stated basis is one people stop trusting (D4). Null where
   * nobody has said.
   */
  basis: z.string().nullable().default(null),
  /**
   * Each pro club's bye week this season, by club code, for a player whose row
   * has left `available` because somebody drafted him: a roster's shared byes
   * are read from here. Unchanging for a draft, so no event carries it.
   */
  byeWeeks: z.record(z.string(), z.number().int()).default({}),
  /** ISO; the room opens 30 minutes before. */
  scheduledAt: Iso.nullable(),
  roomOpen: z.boolean(),
  seats: z.array(DraftSeat),
  /** Whose pick, with the server deadline. Null in the lobby and when complete. */
  current: z
    .object({
      overall: z.number().int(),
      round: z.number().int(),
      teamId: z.string(),
      isMine: z.boolean(),
      deadlineAt: Iso.nullable(),
      /**
       * The seat on the clock is a bot, and the pick just made took the
       * player it would have taken (B-529): its face winces a moment. Never
       * said of a person's seat, whose list is theirs; a client knows its own.
       */
      sniped: z.boolean().default(false),
    })
    .nullable(),
  /** The next three picks after the current one. */
  upcoming: z.array(z.object({ overall: z.number().int(), teamId: z.string() })),
  board: z.array(DraftBoardCell),
  available: z.array(DraftPlayerRow),
  myTeamId: z.string().nullable(),
  myQueue: z.array(PlayerSummary),
  /**
   * My roster by slot as the draft fills it: every starting and bench cell,
   * then any player past them on the bench, which only a dynasty's roster
   * carried whole into its draft has (PLAN-2026-10-27-the-next-season §2.4).
   */
  myRoster: z.array(
    z.object({ slot: z.string(), slotName: z.string(), player: PlayerSummary.nullable() }),
  ),
  myNeeds: z.array(z.string()),
  chat: z.array(DraftChatMessage),
  isCommissioner: z.boolean(),
  lastPick: DraftBoardCell.nullable(),
  /**
   * The hours the pick clock does not run, wall-clock in `zone` (C7,
   * B-341): picks still count then, and no clock runs out. A client says the
   * clock is stopped while the zone's time is inside it. Null for a clock
   * that never stops.
   */
  overnightPause: z
    .object({ from: z.string(), to: z.string(), zone: z.string() })
    .nullable()
    .default(null),
  /**
   * A league mock's room (MOCK_DRAFT.md §6.2), not the league's draft: its
   * seats are taken in the lobby, an open one says `Open seat`, and whoever
   * opened it runs it. Null for the league's own room.
   */
  mockRoom: z
    .object({
      mockId: z.string(),
      /** Who opened it, and runs it. */
      host: z.string(),
      isHost: z.boolean(),
      /** When a lobby nobody starts closes. */
      closesAt: Iso.nullable(),
    })
    .nullable()
    .default(null),
  /** Trading during the draft: whether the room takes offers, mine, and the trades made. */
  trading: DraftTrading.default({ allowed: false, offers: [], trades: [] }),
  /** An auction's half: the players up, the nomination and every team's money. Null for a pick draft. */
  auction: DraftAuction.nullable().default(null),
  updatedAt: Iso,
});
export type DraftView = z.infer<typeof DraftView>;
export type DraftSeat = z.infer<typeof DraftSeat>;
export type DraftBoardCell = z.infer<typeof DraftBoardCell>;
export type DraftPlayerRow = z.infer<typeof DraftPlayerRow>;
export type DraftChatMessage = z.infer<typeof DraftChatMessage>;

// ── Me ────────────────────────────────────────────────────────────────────

const AvatarHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * A generated avatar (a seed the client renders), an uploaded image, or a
 * face the person made (`face`, PLAN-2026-10-24-faces-round-three §7, added
 * 2026-10-24): the face, and the two colors of the disc it is drawn on, as a
 * team's are. A face wearing a part its person has not earned is refused.
 *
 * Other people see the face where a league's reads carry it as a
 * `PersonFace`: a chat line's writer and the members list (B-525).
 */
export const AvatarRef = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('generated'), seed: z.string() }),
  z.object({ kind: z.literal('asset'), assetId: z.string() }),
  z.object({
    kind: z.literal('face'),
    face: Face,
    colors: z.object({ primary: AvatarHex, secondary: AvatarHex }),
    /** The generated avatar's seed, kept so the initial comes back in its own color. */
    seed: z.string().optional(),
  }),
]);
export type AvatarRef = z.infer<typeof AvatarRef>;

/** One way to sign in (ACCOUNTS.md). The identifier is shown only on the account page. */
export const LoginRef = z.object({
  id: z.string(),
  kind: z.enum(['password', 'magic-link', 'google', 'apple']),
  identifier: z.string(),
  verified: z.boolean() /**
   * The address this login is changing to, waiting on the link sent there
   * (PLAN-2026-10-28-by-email §3.5); null with no change waiting. Optional
   * because it is additive.
   */,
  changingTo: z.string().nullable().optional(),
});
export type LoginRef = z.infer<typeof LoginRef>;

/**
 * One device this account is signed in on (`GET /api/me/sessions`).
 *
 * WRITTEN DOWN ON 2026-09-17 because it was the one shape this API served
 * that had no schema anywhere (audit 3.3): the identity package had a
 * `SessionRecord` interface, the endpoint returned it, and the account screen
 * re-typed the same five fields by hand. Three declarations of one shape, and
 * the generated JSON Schema a second client reads from would have had none of
 * them.
 *
 * NO TOKEN, EVER — not even a prefix of one. The id here is the session's
 * database id, which is what `DELETE /api/me/sessions/:id` takes; the token
 * itself is only ever in a cookie or in the one native sign-in response.
 * ICD-S03 §7 invariant 4.
 */
export const SessionRef = z.object({
  id: z.string(),
  /** "Chrome on Windows", or a name a native client gave itself. Null when unknown. */
  device: z.string().nullable(),
  createdAt: Iso,
  expiresAt: Iso,
  /** True for the session making the request, so the list can say "this device". */
  current: z.boolean(),
});
export type SessionRef = z.infer<typeof SessionRef>;

/**
 * What a password change answers (`PUT /api/me/password`, B-244).
 *
 * `signedOut` is counted by the server, because only it knows: a device list
 * a screen loaded a minute ago can be short of a device that signed in since,
 * and "Your other device is signed out" has to be true when it is said.
 * `sessions` is what is left, with this device marked `current` — the
 * session that replaced this one's token included, when a renewal crossed
 * the change on the wire.
 */
export const PasswordChanged = z.object({
  signedOut: z.number().int().nonnegative(),
  sessions: z.array(SessionRef),
});
export type PasswordChanged = z.infer<typeof PasswordChanged>;

export const MeView = z.object({
  /** Identity is the id; everything else here can change. */
  user: z.object({
    id: z.string(),
    displayName: z.string(),
    avatar: AvatarRef,
    /**
     * The zone this person reads every time in (B-181), an IANA name. Null
     * for an account made before it could be chosen: the client asks them to
     * pick one. Optional because it is additive (a server before 2026-09-25
     * does not send it).
     */
    timeZone: z.string().nullable().optional(),
  }),
  logins: z.array(LoginRef),
  // `proMode: boolean` was here until 2026-09-14. The Pro mode boolean became
  // the three-stop detail level on 2026-09-12 and nothing constructed a
  // MeView with it; the level came back as `prefs.detail` on 2026-10-02, when
  // preferences round-trip (`PUT /api/me/prefs`).
  leagues: z.array(LeagueRef),
  /**
   * The account's appearance and layout choices (M2, L3), so every device
   * starts from them on its first request. Null for an account that has
   * never saved any: a client signing in makes its own the account's then.
   * Optional because it is additive (a server before 2026-10-02 does not
   * send it).
   */
  prefs: UserPrefs.nullable().optional(),
  /**
   * Unread notifications, for the badge, so the shell does not make a second
   * request on load (ICD-S04 §4). Optional because it is additive: a client
   * built before 2026-09-18 does not know it, and `null` would claim a count
   * of nothing where the truth is that this server did not say.
   */
  unread: z.number().int().min(0).optional(),
  /**
   * The account's achievements not yet shown to it (ACHIEVEMENTS.md §12),
   * newest first, so the moment arrives on `/api/me`'s rhythm with no request
   * of its own and shows once on any device: a client that shows them says so
   * with `POST /api/me/achievements/seen`. Found late by a backfill, they say
   * `retro` and are shown together, never one card each. Optional because it
   * is additive (a server before A4 does not send it).
   */
  unlocked: z.array(FoundAchievement).optional(),
  /**
   * This session, as the client that holds it needs to see it.
   *
   * `expiresAt` is for every client and is the honest answer to "how long am
   * I signed in for". A browser rarely needs it — renewal is invisible and
   * the cookie carries its own `Max-Age` — but a native client has no cookie
   * to expire and nothing else to read.
   *
   * `token` is set on exactly one response: the sign-up or sign-in of a
   * caller that said `native: true`. See the header of `auth.ts` for why the
   * "no token in a view" rule has this one exception and how narrow it is.
   */
  session: z.object({
    expiresAt: z.string(),
    token: z.string().optional(),
  }),
});
export type MeView = z.infer<typeof MeView>;

/**
 * `GET /api/me/pulse` — what a client asks on a timer while a page is open
 * (2026-10-10): the three parts of `MeView` that change while somebody sits on
 * a page — the bell's count, the achievements not yet shown, the account's
 * choices made on another device — without the account and its leagues
 * around them, which a page does not need again every half minute. Never
 * cached; it writes nothing but what `/api/me` would on the account's first
 * birthday.
 */
export const MePulse = z.object({
  /** Whose pulse it is: an answer for somebody since signed out is not one for who is in now. */
  userId: z.string(),
  /** As `MeView.unread`. */
  unread: z.number().int().min(0),
  /** As `MeView.unlocked`: shown on any device, say so with `POST /api/me/achievements/seen`. */
  unlocked: z.array(FoundAchievement),
  /** As `MeView.prefs`: null for an account that has never saved any. */
  prefs: UserPrefs.nullable(),
  /**
   * The milestones the teams this person runs earned and they have not been
   * shown, newest first (PLAN-2026-10-29-milestones-as-they-happen §2): shown
   * on any device, say so with `POST /api/me/milestones/seen`. Added
   * 2026-10-29; a server before it does not send it.
   */
  milestones: z.array(MilestoneMoment).default([]),
});
export type MePulse = z.infer<typeof MePulse>;

/** `POST /api/me/milestones/seen` — these were shown; ids not waiting for the person are left alone. */
export const MilestonesSeenRequest = z.object({
  ids: z.array(z.string().min(1).max(120)).min(1).max(100),
});
export type MilestonesSeenRequest = z.infer<typeof MilestonesSeenRequest>;

/** How many of them were marked now; none when another device got there first. */
export const MilestonesSeen = z.object({ marked: z.number().int().min(0) });
export type MilestonesSeen = z.infer<typeof MilestonesSeen>;
